<template>
  <f7-page name="control-cifras-oeb-plan" @page:init="onPageInit">
    <f7-navbar>
      <f7-nav-left><f7-link back icon-f7="arrow_left"></f7-link></f7-nav-left>
      <f7-nav-title>Control de cifras OEB — Planificaci&oacute;n</f7-nav-title>
      <f7-nav-right>
        <f7-link @click="abrirGuiaPdf" style="margin-right:4px"><span style="background:linear-gradient(135deg,#dc3545,#c82333);color:#fff;border-radius:4px;padding:2px 8px;font-size:10px;font-weight:700;display:inline-flex;align-items:center;gap:3px">PDF</span></f7-link>
        <f7-link @click="popupInfo = !popupInfo; if (popupInfo && !instrucciones.length) cargarInstrucciones()" style="margin-right:4px"><span style="background:linear-gradient(135deg,#0F6E56,#27ae80);color:#fff;border-radius:50%;width:24px;height:24px;display:inline-flex;align-items:center;justify-content:center;font-size:13px;font-weight:700">i</span></f7-link>
        <f7-button small fill color="green" @click="guardarTodosNumIc" :disabled="guardandoIc" style="margin-right:8px;font-size:10px">Guardar I/C</f7-button>
        <f7-link @click="zoomOut" style="font-size:12px;min-width:20px">&minus;</f7-link>
        <span style="font-size:11px;min-width:30px;text-align:center">{{ zoom }}%</span>
        <f7-link @click="zoomIn" style="font-size:12px;min-width:20px">+</f7-link>
        <f7-link icon-f7="arrow_2_circlepath" @click="actualizarCifras" title="Actualizar cifras"></f7-link>
      </f7-nav-right>
    </f7-navbar>
    <!-- #16 · La ficha de planificacion se recompone entera en cada peticion: sin cache. -->
    <div class="sello-datos-bar"><sello-datos estado="vivo" /></div>

    <f7-page-content style="padding-top:0" v-roles="['ADMIN','DIRECTOR','DTYCAL','PLANIFICACION']">
      <div class="contenido-plan" :style="{ fontSize: (zoom * 0.12) + 'px' }">

        <!-- SELECTOR DE MES -->
        <div class="mes-selector">
          <f7-link @click="mesPrevio" icon-f7="chevron_left"></f7-link>
          <select v-model="anio" @change="cargarFicha" class="select-anio">
            <option v-for="a in aniosDisponibles" :key="a" :value="a">{{ a }}</option>
          </select>
          <select v-model="mes" @change="cargarFicha" class="select-mes">
            <option v-for="(nombre, idx) in nombresMeses" :key="idx" :value="idx + 1">{{ nombre }}</option>
          </select>
          <span class="mes-badge" :class="'badge-' + (ficha ? ficha.estado : '')">{{ ficha ? ficha.estado : '' }}</span>
          <f7-link @click="mesSiguiente" icon-f7="chevron_right"></f7-link>
        </div>

        <!-- KPIs SUPERIORES -->
        <div class="metricas-grid" v-if="ficha && ficha.totales">
          <div class="metrica-card">
            <div class="metrica-label">Progresi&oacute;n OEB <info-icon dominio="oeb" clave="OEB_PROGRESION" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div>
            <div class="metrica-valor">{{ formatImporte(ficha.totales.progresionTotal) }}</div>
          </div>
          <div class="metrica-card">
            <div class="metrica-label">Objetivo mes <info-icon dominio="oeb" clave="OEB_ObjetivoMes" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div>
            <div class="metrica-valor">{{ formatImporte(ficha.totales.objetivoMensualTotal) }}</div>
          </div>
          <div class="metrica-card">
            <div class="metrica-label">Falta para llegar <info-icon dominio="oeb" clave="OEB_FaltaTotal" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div>
            <div class="metrica-valor" :class="ficha.totales.faltaTotal >= 0 ? 'valor-positivo' : 'valor-negativo'">
              {{ formatImporteConSigno(ficha.totales.faltaTotal) }}
            </div>
          </div>
          <div class="metrica-card">
            <div class="metrica-label">% cumplimiento <info-icon dominio="oeb" clave="OEB_PctCumplimiento" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div>
            <div class="metrica-valor">
              <span class="badge-estado" :class="'estado-' + calcularEstadoColor(ficha.totales.porcentajeGlobal)">{{ formatPorcentaje(ficha.totales.porcentajeGlobal) }}</span>
            </div>
          </div>
        </div>

        <!-- BARRA INFO -->
        <div class="info-actualizacion" v-if="ficha">
          <span>&Uacute;ltima actualizaci&oacute;n: {{ formatFecha(ficha.modificationDate) }}</span>
          <span> &middot; Cada 30 min &middot; Datos: calendario + reparto ejecuci&oacute;n</span>
        </div>

        <!-- TOOLBAR TOGGLES + ACCIONES -->
        <div class="toolbar-plan" v-if="ficha">
          <div class="toggles-row">
            <div class="toggle-btn" :style="toggleStyle('#1a2744', toggles.oeb)" @click="alternarToggle('oeb')" style="display:inline-flex;align-items:center;gap:3px">OEB<info-icon dominio="oeb" clave="OEB_OEB" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div>
            <div class="toggle-btn" :style="toggleStyle('#198754', toggles.oebR)" @click="alternarToggle('oebR')" style="display:inline-flex;align-items:center;gap:3px">OEB-R<info-icon dominio="oeb" clave="OEB_OEB_R" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div>
            <div class="toggle-btn" :style="toggleStyle('#0d6efd', toggles.oebP)" @click="alternarToggle('oebP')" style="display:inline-flex;align-items:center;gap:3px">OEB-P<info-icon dominio="oeb" clave="OEB_OEB_P" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div>
            <div class="toggle-btn" :style="toggleStyle('#e6a817', toggles.oebPPend)" @click="alternarToggle('oebPPend')" style="display:inline-flex;align-items:center;gap:3px">OEB-PPEND<info-icon dominio="oeb" clave="OEB_OEB_PPEND" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div>
            <div class="toggle-btn" :style="toggleStyle('#dc3545', toggles.tpc)" @click="alternarToggle('tpc')" style="display:inline-flex;align-items:center;gap:3px">TPC(SI)<info-icon dominio="oeb" clave="OEB_TPC_SI" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div>
            <div class="toggle-btn" :style="toggleStyle('#7B2D8E', toggles.difTpc)" @click="alternarToggle('difTpc')" style="display:inline-flex;align-items:center;gap:3px">&Delta;TPC<info-icon dominio="oeb" clave="OEB_DTPC" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div>
            <div class="toggle-btn" :style="toggleStyle('#6D4C41', toggles.infPend)" @click="alternarToggle('infPend')" style="display:inline-flex;align-items:center;gap:3px">INF.PEND.<info-icon dominio="oeb" clave="OEB_INF_PEND" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" :concepto="conceptoInfPend" /></div>
          </div>
          <div class="acciones-row">
            <div class="toggle-btn toggle-detalle" :style="toggleStyle('#555', mostrarDetalle)" @click="mostrarDetalle = !mostrarDetalle">
              {{ mostrarDetalle ? '&minus; Ocultar detalle' : '+ Detalle semanas' }}
            </div>
            <!-- #85 · Los botones que escriben en el mes desaparecen mientras se carga: recargar el
                 mismo mes conserva la ficha hasta que llega la nueva (#769), y esa ficha está a punto
                 de ser sustituida. Los toggles y el detalle se quedan, que solo cambian lo que se
                 pinta. -->
            <f7-button small fill style="background:#198754" @click="crearMesSiguiente" v-if="!cargando" v-roles="['ADMIN','DIRECTOR','DTYCAL']">+ Crear {{ nombreMesSiguiente }}</f7-button>
            <!-- #34 · Guardar y + Persona solo para Administrador y Director. DTYCAL y
                 PLANIFICACION siguen entrando a la pantalla, pero solo a mirar. -->
            <f7-button small fill color="blue" @click="guardarPersonasVisibles" :disabled="guardandoPersonas" v-if="!cargando" v-roles="['ADMIN','DIRECTOR']">{{ guardandoPersonas ? 'Guardando...' : 'Guardar' }}</f7-button>
            <f7-button small fill style="background:#5a2d8f" @click="agregarPersona" v-if="!cargando" v-roles="['ADMIN','DIRECTOR']">+ Persona</f7-button>
            <f7-button small outline @click="enviarReporte" v-roles="['ADMIN','DIRECTOR','DTYCAL']">Enviar reporte</f7-button>
            <f7-button small outline @click="irANotificaciones" v-roles="['ADMIN','DIRECTOR','DTYCAL']">Programar env&iacute;os</f7-button>
          </div>
        </div>

        <!-- LEYENDA CIFRAS -->
        <div class="leyenda-cifras" v-if="ficha">
          <span class="ley-item"><span class="ley-dot" style="background:#1a2744"></span> OEB total <info-icon dominio="oeb" clave="OEB_OEB" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></span>
          <span class="ley-item"><span class="ley-dot" style="background:#198754"></span> OEB-R realizado <info-icon dominio="oeb" clave="OEB_OEB_R" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></span>
          <span class="ley-item"><span class="ley-dot" style="background:#0d6efd"></span> OEB-P planificado <info-icon dominio="oeb" clave="OEB_OEB_P" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></span>
          <span class="ley-item"><span class="ley-dot" style="background:#e6a817"></span> OEB-PPEND pendiente <info-icon dominio="oeb" clave="OEB_OEB_PPEND" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></span>
          <span class="ley-item"><span class="ley-dot" style="background:#dc3545"></span> TPC(SI) reparto ejec. <info-icon dominio="oeb" clave="OEB_TPC_SI" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></span>
          <span class="ley-item"><span class="ley-dot" style="background:#7B2D8E"></span> &Delta;TPC diferencia TPC(SI)-OEB <info-icon dominio="oeb" clave="OEB_DTPC" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></span>
          <span class="ley-item"><span class="ley-dot" style="background:#6D4C41"></span> INF.PEND. informe ejecutado sin emitir <info-icon dominio="oeb" clave="OEB_INF_PEND" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" :concepto="conceptoInfPend" /></span>
        </div>

        <!-- INF.PEND. se pide aparte al encender el botón (#45), así que hay un momento en el que la
             columna está vacía. Sin decirlo, un hueco en blanco se leería como "no tiene pendiente". -->
        <div class="aviso-infpend" v-if="toggles.infPend && cargandoInfPend">
          Calculando INF.PEND&hellip;
        </div>

        <!-- La foto de las semanas pasadas se reconstruye con la Fecha Informe de la línea. Si hay
             líneas emitidas sin esa fecha no se pueden situar en el tiempo y se quedan fuera, así que
             se dice: una cifra algo corta que no se sabe que lo está es peor que un aviso. -->
        <div class="aviso-infpend" v-if="toggles.infPend && !cargandoInfPend && infPendSinFecha > 0">
          &#9888; {{ infPendSinFecha }} l&iacute;nea(s) con el informe ya emitido pero sin Fecha Informe: no se pueden
          situar en el tiempo y no cuentan en la foto de las semanas cerradas. La semana en curso no se ve afectada.
        </div>

        <!-- INDICADORES GLOBALES -->
        <div class="indicadores-grid" v-if="ficha && ficha.personas">
          <div class="ind-card ind-gris"><div class="ind-label">Personas <info-icon dominio="oeb" clave="OEB_Personas" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div><div class="ind-val">{{ ficha.personas.length }}</div></div>
          <div class="ind-card ind-gris"><div class="ind-label">D&iacute;as h&aacute;biles <info-icon dominio="oeb" clave="OEB_DH" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div><div class="ind-val">{{ indTotalDH }}</div></div>
          <div class="ind-card ind-amarillo"><div class="ind-label">Fiestas <info-icon dominio="oeb" clave="OEB_Fiestas" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div><div class="ind-val">{{ indTotalFiestas }}</div></div>
          <div class="ind-card ind-amarillo"><div class="ind-label">Vacaciones <info-icon dominio="oeb" clave="OEB_VAC" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div><div class="ind-val">{{ indTotalVacaciones }}</div></div>
          <div class="ind-card ind-rojo"><div class="ind-label">Bajas laborales <info-icon dominio="oeb" clave="OEB_BL" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div><div class="ind-val">{{ indTotalBajas }}</div></div>
          <div class="ind-card ind-gris"><div class="ind-label">D&iacute;as reales <info-icon dominio="oeb" clave="OEB_DR" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div><div class="ind-val">{{ indTotalDR }}</div></div>
          <div class="ind-card ind-gris"><div class="ind-label">N&ordm; I/C mes <info-icon dominio="oeb" clave="OEB_NumIC_Mes" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div><div class="ind-val">{{ indTotalNumIc.toFixed(1) }}</div></div>
          <div class="ind-card ind-azul"><div class="ind-label">N&ordm; horas mes <info-icon dominio="oeb" clave="OEB_NumHIC_Mes" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div><div class="ind-val">{{ indTotalHorasMes }}</div></div>
          <div class="ind-card ind-azul"><div class="ind-label">Precio medio/h <info-icon dominio="oeb" clave="OEB_PrecioMedioICH_Mes" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div><div class="ind-val">{{ indPrecioMedioHora }} &euro;</div></div>
          <div class="ind-card ind-amarillo"><div class="ind-label">Horas comp. <info-icon dominio="oeb" clave="OEB_CO" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div><div class="ind-val">{{ indTotalCompensacion }}</div></div>
          <div class="ind-card ind-azul"><div class="ind-label">Productividad I/C <info-icon dominio="oeb" clave="OEB_ProdIc_Mes" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></div><div class="ind-val">{{ formatImporteCorto(indProductividadIc) }}</div></div>
        </div>

        <!-- TABLA PRINCIPAL -->
        <div class="tabla-container" v-if="ficha && ficha.personas && ficha.personas.length > 0">
          <div class="tabla-scroll">
            <table class="tabla-plan">
              <thead>
                <tr>
                  <th class="col-sticky col-codigo">C&Oacute;D.</th>
                  <template v-for="sem in ficha.calendario.semanas">
                    <th class="col-multi col-sep-left" :key="'h-'+sem.numero">
                      S{{ sem.numero }}<br><span class="sem-fechas">{{ extraerDias(sem) }}</span>
                    </th>
                    <template v-if="mostrarDetalle">
                      <th class="col-det" :key="'hd1-'+sem.numero" style="background:#2d5f8a">Falta<br>obj.sem <info-icon dominio="oeb" clave="OEB_FaltaObjSem" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                      <th class="col-det" :key="'hd2-'+sem.numero" style="background:#b8860b">PPend <info-icon dominio="oeb" clave="OEB_OEB_PPEND" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                      <th class="col-det" :key="'hd3-'+sem.numero" style="background:#555">Disp.<br>horas <info-icon dominio="oeb" clave="OEB_DispHoras" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                    </template>
                  </template>
                  <th class="col-res col-sep-left">PROGR. <info-icon dominio="oeb" clave="OEB_PROGRESION" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-res">OBJ. <info-icon dominio="oeb" clave="OEB_ObjetivoMes" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-res">FALTA <info-icon dominio="oeb" clave="OEB_FaltaTotal" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-pct">% <info-icon dominio="oeb" clave="OEB_PctCumplimiento" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-num col-sep-left" style="text-align:center;font-size:9px">TPC(SI)<br>&euro; <info-icon dominio="oeb" clave="OEB_TPC_SI" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-falta-tpc col-sep-left" style="font-size:9px">FALTA<br>TPC(SI) <info-icon dominio="oeb" clave="OEB_FaltaTPC" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-dia col-sep-left">D.H <info-icon dominio="oeb" clave="OEB_DH" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-dia">Vac <info-icon dominio="oeb" clave="OEB_VAC" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-dia">BL <info-icon dominio="oeb" clave="OEB_BL" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-dia">Comp. <info-icon dominio="oeb" clave="OEB_CO" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-dia">D.R <info-icon dominio="oeb" clave="OEB_DR" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-dia col-sep-left" v-for="sem in ficha.calendario.semanas" :key="'hd-'+sem.numero">S{{ sem.numero }}</th>
                  <th class="col-num col-sep-left" style="text-align:center">T.D&Iacute;A <info-icon dominio="oeb" clave="OEB_TDIA" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-num" style="text-align:center">H.D. <info-icon dominio="oeb" clave="OEB_HD" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-num" style="text-align:center">N&ordm; I/C <info-icon dominio="oeb" clave="OEB_NumIC" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-num" style="text-align:center">Prod/<br>d&iacute;a <info-icon dominio="oeb" clave="OEB_ProdDia_Mes" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-num" style="text-align:center">Prod/<br>h <info-icon dominio="oeb" clave="OEB_ProdH_Mes" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></th>
                  <th class="col-sem col-sep-left" v-for="sem in ficha.calendario.semanas" :key="'ho-'+sem.numero">S{{ sem.numero }}</th>
                  <th class="col-num col-sep-left">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="persona in personasOrdenadas" :key="persona.codigoPersonal"
                    :class="personasOrdenadas.indexOf(persona) % 2 === 1 ? 'fila-alt' : ''" class="fila-persona">
                  <td class="col-sticky col-codigo" style="cursor:pointer" @click="abrirDetallePersona(persona)"><strong>{{ persona.codigoPersonal }}</strong></td>
                  <template v-for="(sem, idx) in ficha.calendario.semanas">
                    <td class="col-multi col-sep-left" :key="'c-'+persona.codigoPersonal+'-'+idx"
                        style="cursor:pointer;position:relative"
                        @click.stop="abrirDesgloseDiario(persona, idx)">
                      <span class="triangulo-m" :class="{'triangulo-override': tieneOverride(persona.codigoPersonal, idx)}" @click.stop="abrirManualOverride(persona, idx)"><span>M</span></span>
                      <div v-if="numToggleActivos <= 3" class="multi-center">
                        <div v-if="toggles.oeb" class="val-line"><span class="vdot" style="background:#1a2744"></span><span>{{ fmtCelda(persona.resultadosSemana ? persona.resultadosSemana[idx] : 0) }}</span></div>
                        <div v-if="toggles.oebR" class="val-line"><span class="vdot" style="background:#198754"></span><span>{{ fmtCelda(getDesgloseVal(persona.codigoPersonal, 'oebR', idx)) }}</span></div>
                        <div v-if="toggles.oebP" class="val-line"><span class="vdot" style="background:#0d6efd"></span><span>{{ fmtCelda(getDesgloseVal(persona.codigoPersonal, 'oebP', idx)) }}</span></div>
                        <div v-if="toggles.oebPPend" class="val-line"><span class="vdot" style="background:#e6a817"></span><span>{{ fmtCelda(getDesgloseVal(persona.codigoPersonal, 'oebPPend', idx)) }}</span></div>
                        <div v-if="toggles.tpc" class="val-line"><span class="vdot" style="background:#dc3545"></span><span>{{ fmtCelda(getTpcVal(persona.codigoPersonal, idx)) }}</span></div>
                        <div v-if="toggles.difTpc" class="val-line val-dtpc" :class="difTpcSemClase(persona, idx)" @click.stop="abrirAnalisisDiferencias(persona, idx)"><span class="vdot" style="background:#7B2D8E"></span><span>{{ difTpcSemTexto(persona, idx) }}</span></div>
                        <div v-if="toggles.infPend" class="val-line" :title="infPendTitulo(idx)"><span class="vdot" style="background:#6D4C41" v-if="infPendConDato(idx)"></span><span>{{ infPendSemTexto(persona, idx) }}</span></div>
                      </div>
                      <div v-else-if="numToggleActivos <= 5" class="multi-split">
                        <div class="cell-left">
                          <div v-if="toggles.oeb" class="val-line"><span class="vdot" style="background:#1a2744"></span><span>{{ fmtCelda(persona.resultadosSemana ? persona.resultadosSemana[idx] : 0) }}</span></div>
                          <div v-if="toggles.oebR" class="val-line"><span class="vdot" style="background:#198754"></span><span>{{ fmtCelda(getDesgloseVal(persona.codigoPersonal, 'oebR', idx)) }}</span></div>
                          <div v-if="toggles.oebP" class="val-line"><span class="vdot" style="background:#0d6efd"></span><span>{{ fmtCelda(getDesgloseVal(persona.codigoPersonal, 'oebP', idx)) }}</span></div>
                        </div>
                        <div class="cell-right">
                          <div v-if="toggles.oebPPend" class="val-line"><span class="vdot" style="background:#e6a817"></span><span>{{ fmtCelda(getDesgloseVal(persona.codigoPersonal, 'oebPPend', idx)) }}</span></div>
                          <div v-if="toggles.tpc" class="val-line"><span class="vdot" style="background:#dc3545"></span><span>{{ fmtCelda(getTpcVal(persona.codigoPersonal, idx)) }}</span></div>
                          <div v-if="toggles.difTpc" class="val-line val-dtpc" :class="difTpcSemClase(persona, idx)" @click.stop="abrirAnalisisDiferencias(persona, idx)"><span class="vdot" style="background:#7B2D8E"></span><span>{{ difTpcSemTexto(persona, idx) }}</span></div>
                          <div v-if="toggles.infPend" class="val-line" :title="infPendTitulo(idx)"><span class="vdot" style="background:#6D4C41" v-if="infPendConDato(idx)"></span><span>{{ infPendSemTexto(persona, idx) }}</span></div>
                        </div>
                      </div>
                      <div v-else class="multi-split multi-split-6">
                        <div class="cell-left">
                          <div v-if="toggles.oeb" class="val-line"><span class="vdot" style="background:#1a2744"></span><span>{{ fmtCelda(persona.resultadosSemana ? persona.resultadosSemana[idx] : 0) }}</span></div>
                          <div v-if="toggles.oebR" class="val-line"><span class="vdot" style="background:#198754"></span><span>{{ fmtCelda(getDesgloseVal(persona.codigoPersonal, 'oebR', idx)) }}</span></div>
                          <div v-if="toggles.oebP" class="val-line"><span class="vdot" style="background:#0d6efd"></span><span>{{ fmtCelda(getDesgloseVal(persona.codigoPersonal, 'oebP', idx)) }}</span></div>
                        </div>
                        <div class="cell-right">
                          <div v-if="toggles.oebPPend" class="val-line"><span class="vdot" style="background:#e6a817"></span><span>{{ fmtCelda(getDesgloseVal(persona.codigoPersonal, 'oebPPend', idx)) }}</span></div>
                          <div v-if="toggles.tpc" class="val-line"><span class="vdot" style="background:#dc3545"></span><span>{{ fmtCelda(getTpcVal(persona.codigoPersonal, idx)) }}</span></div>
                          <div v-if="toggles.difTpc" class="val-line val-dtpc" :class="difTpcSemClase(persona, idx)" @click.stop="abrirAnalisisDiferencias(persona, idx)"><span class="vdot" style="background:#7B2D8E"></span><span>{{ difTpcSemTexto(persona, idx) }}</span></div>
                          <div v-if="toggles.infPend" class="val-line" :title="infPendTitulo(idx)"><span class="vdot" style="background:#6D4C41" v-if="infPendConDato(idx)"></span><span>{{ infPendSemTexto(persona, idx) }}</span></div>
                        </div>
                      </div>
                    </td>
                    <template v-if="mostrarDetalle">
                      <td class="col-det" :key="'d1-'+persona.codigoPersonal+'-'+idx"
                          :class="faltaSemClase(persona, idx)">{{ faltaSemTexto(persona, idx) }}</td>
                      <td class="col-det col-det-ppend" :key="'d2-'+persona.codigoPersonal+'-'+idx">
                        {{ fmtCelda(getDesgloseVal(persona.codigoPersonal, 'oebPPend', idx)) }}
                      </td>
                      <td class="col-det col-det-horas" :key="'d3-'+persona.codigoPersonal+'-'+idx"
                          :class="dispHorasClase(persona, idx)" style="cursor:pointer"
                          @click.stop="abrirDispHoras(persona, idx)">{{ dispHorasTexto(persona, idx) }}</td>
                    </template>
                  </template>
                  <td class="col-res col-sep-left" :style="colorProgresion(persona)" style="cursor:pointer" @click="abrirDetallePersona(persona)">
                    <strong>{{ formatImporteCorto(persona.progresionMes) }}</strong>
                  </td>
                  <td class="col-res" style="cursor:pointer" @click="abrirDetallePersona(persona)">{{ formatImporteCorto(persona.objetivoMensual) }}</td>
                  <td class="col-res" :class="persona.faltaParaLlegar >= 0 ? 'valor-positivo' : 'valor-negativo'" style="cursor:pointer" @click="abrirDetallePersona(persona)">
                    {{ formatImporteConSigno(persona.faltaParaLlegar) }}
                  </td>
                  <td class="col-pct" style="cursor:pointer" @click="abrirDetallePersona(persona)">
                    <span class="badge-estado" :class="'estado-' + persona.estadoColor">{{ formatPorcentaje(persona.porcentajeCumplimiento) }}</span>
                  </td>
                  <td class="col-num col-sep-left" style="text-align:right;cursor:pointer" @click="abrirDetallePersona(persona)">{{ Math.round(sumaTpcPersona(persona.codigoPersonal)) }}</td>
                  <td class="col-falta-tpc col-sep-left" :class="faltaTpcClase(persona)" style="cursor:pointer" @click="abrirDetallePersona(persona)">{{ faltaTpcTexto(persona) }}</td>
                  <td class="col-dia col-sep-left" :class="{'dia-ausencia': persona.semanas && persona.semanas.some(function(s){ return !s.modificado && s.diasHabiles < s.diasHabilesOriginal; })}" style="cursor:pointer" @click="abrirDetallePersona(persona)">{{ calcularDiasHabilesPersona(persona) }}</td>
                  <td class="col-dia" :class="{'dia-ausencia': persona.diasVacaciones > 0}" style="cursor:pointer" @click="abrirDetallePersona(persona)">{{ persona.diasVacaciones || 0 }}</td>
                  <td class="col-dia" :class="{'dia-baja': getDesgloseBL(persona.codigoPersonal) > 0}" style="cursor:pointer" @click="abrirDetallePersona(persona)">{{ getDesgloseBL(persona.codigoPersonal) }}</td>
                  <td class="col-dia" :class="{'dia-comp': getCompensacionPersona(persona.codigoPersonal) > 0}" style="cursor:pointer" @click="abrirDetallePersona(persona)">{{ getCompensacionPersona(persona.codigoPersonal) > 0 ? getCompensacionPersona(persona.codigoPersonal).toFixed(1) : '' }}</td>
                  <td class="col-dia" :class="{'dia-baja': getDesgloseBL(persona.codigoPersonal) > 0, 'dia-ausencia': !getDesgloseBL(persona.codigoPersonal) && persona.diasVacaciones > 0}" style="cursor:pointer" @click="abrirDetallePersona(persona)">{{ persona.diasReales || 0 }}</td>
                  <td class="col-dia col-sep-left" v-for="(sem, sidx) in persona.semanas" :key="'ds-'+persona.codigoPersonal+'-'+sidx"
                      :class="[sem.modificado ? 'dia-modificado' : getColorDiaSemana(persona.codigoPersonal, sidx)]" style="cursor:pointer" @click="abrirDetallePersona(persona)">{{ getDiasRealesSem(persona.codigoPersonal, sidx) }}</td>
                  <td class="col-num col-sep-left" style="cursor:pointer" @click="abrirDetallePersona(persona)">{{ formatImporteCorto(persona.objetivoDiarioActual) }}</td>
                  <td class="col-num" style="text-align:center"><input type="number" min="0" step="0.5" style="width:40px;text-align:center;border:1px solid #ddd;border-radius:3px;padding:2px;font-size:11px" :value="getHorasDiarias(persona.codigoPersonal)" @change="guardarHorasDiarias(persona.codigoPersonal, $event.target.value)"></td>
                  <td class="col-num" style="text-align:center"><input type="number" min="0" step="0.5" style="width:40px;text-align:center;border:1px solid #ddd;border-radius:3px;padding:2px;font-size:11px" :value="getNumIc(persona.codigoPersonal)" @change="guardarNumIc(persona.codigoPersonal, $event.target.value)"></td>
                  <td class="col-num" style="text-align:right">{{ persona.diasReales > 0 ? Math.round(sumaTpcPersona(persona.codigoPersonal) / persona.diasReales) : 0 }}</td>
                  <td class="col-num" style="text-align:right">{{ getProdHora(persona.codigoPersonal) }}</td>
                  <td class="col-sem col-sep-left" v-for="(obj, oidx) in persona.objetivosSemana" :key="'os-'+persona.codigoPersonal+'-'+oidx" style="cursor:pointer" @click="abrirDetallePersona(persona)">{{ formatImporteCorto(obj) }}</td>
                  <td class="col-num col-sep-left" style="cursor:pointer" @click="abrirDetallePersona(persona)">{{ formatImporteCorto(persona.objetivoMensual) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <!-- FILA TOTAL -->
                <tr class="fila-totales">
                  <td class="col-sticky col-codigo"><strong>TOTAL</strong></td>
                  <template v-for="(sem, idx) in ficha.calendario.semanas">
                    <td class="col-multi col-sep-left" :key="'t-'+idx">
                      <div v-if="numToggleActivos <= 3" class="multi-center">
                        <div v-if="toggles.oeb" class="val-line"><span class="vdot" style="background:#1a2744"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].oeb : 0) }}</strong></div>
                        <div v-if="toggles.oebR" class="val-line"><span class="vdot" style="background:#198754"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].oebR : 0) }}</strong></div>
                        <div v-if="toggles.oebP" class="val-line"><span class="vdot" style="background:#0d6efd"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].oebP : 0) }}</strong></div>
                        <div v-if="toggles.oebPPend" class="val-line"><span class="vdot" style="background:#e6a817"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].oebPPend : 0) }}</strong></div>
                        <div v-if="toggles.tpc" class="val-line"><span class="vdot" style="background:#dc3545"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].tpc : 0) }}</strong></div>
                        <div v-if="toggles.difTpc" class="val-line" :class="totalDifTpcSemClase(idx)"><span class="vdot" style="background:#7B2D8E"></span><strong>{{ totalDifTpcSemTexto(idx) }}</strong></div>
                        <div v-if="toggles.infPend" class="val-line" :title="infPendTitulo(idx)"><span class="vdot" style="background:#6D4C41" v-if="infPendConDato(idx)"></span><strong>{{ totalInfPendSemTexto(idx) }}</strong></div>
                      </div>
                      <div v-else-if="numToggleActivos <= 5" class="multi-split">
                        <div class="cell-left">
                          <div v-if="toggles.oeb" class="val-line"><span class="vdot" style="background:#1a2744"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].oeb : 0) }}</strong></div>
                          <div v-if="toggles.oebR" class="val-line"><span class="vdot" style="background:#198754"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].oebR : 0) }}</strong></div>
                          <div v-if="toggles.oebP" class="val-line"><span class="vdot" style="background:#0d6efd"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].oebP : 0) }}</strong></div>
                        </div>
                        <div class="cell-right">
                          <div v-if="toggles.oebPPend" class="val-line"><span class="vdot" style="background:#e6a817"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].oebPPend : 0) }}</strong></div>
                          <div v-if="toggles.tpc" class="val-line"><span class="vdot" style="background:#dc3545"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].tpc : 0) }}</strong></div>
                          <div v-if="toggles.difTpc" class="val-line" :class="totalDifTpcSemClase(idx)"><span class="vdot" style="background:#7B2D8E"></span><strong>{{ totalDifTpcSemTexto(idx) }}</strong></div>
                          <div v-if="toggles.infPend" class="val-line" :title="infPendTitulo(idx)"><span class="vdot" style="background:#6D4C41" v-if="infPendConDato(idx)"></span><strong>{{ totalInfPendSemTexto(idx) }}</strong></div>
                        </div>
                      </div>
                      <div v-else class="multi-split multi-split-6">
                        <div class="cell-left">
                          <div v-if="toggles.oeb" class="val-line"><span class="vdot" style="background:#1a2744"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].oeb : 0) }}</strong></div>
                          <div v-if="toggles.oebR" class="val-line"><span class="vdot" style="background:#198754"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].oebR : 0) }}</strong></div>
                          <div v-if="toggles.oebP" class="val-line"><span class="vdot" style="background:#0d6efd"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].oebP : 0) }}</strong></div>
                        </div>
                        <div class="cell-right">
                          <div v-if="toggles.oebPPend" class="val-line"><span class="vdot" style="background:#e6a817"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].oebPPend : 0) }}</strong></div>
                          <div v-if="toggles.tpc" class="val-line"><span class="vdot" style="background:#dc3545"></span><strong>{{ fmtCelda(semTotales[idx] ? semTotales[idx].tpc : 0) }}</strong></div>
                          <div v-if="toggles.difTpc" class="val-line" :class="totalDifTpcSemClase(idx)"><span class="vdot" style="background:#7B2D8E"></span><strong>{{ totalDifTpcSemTexto(idx) }}</strong></div>
                          <div v-if="toggles.infPend" class="val-line" :title="infPendTitulo(idx)"><span class="vdot" style="background:#6D4C41" v-if="infPendConDato(idx)"></span><strong>{{ totalInfPendSemTexto(idx) }}</strong></div>
                        </div>
                      </div>
                    </td>
                    <template v-if="mostrarDetalle">
                      <td class="col-det" :key="'td1-'+idx" :class="totalFaltaSemClase(idx)">{{ totalFaltaSemTexto(idx) }}</td>
                      <td class="col-det" :key="'td2-'+idx">{{ fmtCelda(semTotales[idx] ? semTotales[idx].oebPPend : 0) }}</td>
                      <td class="col-det" :key="'td3-'+idx">&mdash;</td>
                    </template>
                  </template>
                  <td class="col-res col-sep-left"><strong>{{ formatImporteCorto(ficha.totales.progresionTotal) }}</strong></td>
                  <td class="col-res"><strong>{{ formatImporteCorto(ficha.totales.objetivoMensualTotal) }}</strong></td>
                  <td class="col-res" :class="ficha.totales.faltaTotal >= 0 ? 'valor-positivo' : 'valor-negativo'">
                    <strong>{{ formatImporteConSigno(ficha.totales.faltaTotal) }}</strong>
                  </td>
                  <td class="col-pct">
                    <span class="badge-estado" :class="'estado-' + calcularEstadoColor(ficha.totales.porcentajeGlobal)">{{ formatPorcentaje(ficha.totales.porcentajeGlobal) }}</span>
                  </td>
                  <td class="col-num col-sep-left" style="text-align:right;font-weight:600">{{ Math.round(indTotalTpc) }}</td>
                  <td class="col-falta-tpc col-sep-left" :class="totalFaltaTpcVal >= 0 ? 'valor-positivo' : 'valor-negativo'"><strong>{{ formatImporteConSigno(totalFaltaTpcVal) }}</strong></td>
                  <td colspan="5" class="col-sep-left"></td>
                  <td class="col-dia col-sep-left" v-for="sem in ficha.calendario.semanas" :key="'td-'+sem.numero"></td>
                  <td class="col-sep-left"></td>
                  <td class="col-num"></td>
                  <td class="col-num" style="text-align:center;font-weight:600">{{ indTotalNumIc.toFixed(1) }}</td>
                  <td class="col-num" style="text-align:right;font-weight:600">{{ indTotalDR > 0 ? Math.round(indTotalTpc / indTotalDR) : 0 }}</td>
                  <td class="col-num" style="text-align:right;font-weight:600">{{ indTotalDR > 0 && indAvgHD > 0 ? Math.round(indTotalTpc / indTotalDR / indAvgHD) : 0 }}</td>
                  <td class="col-sem col-sep-left" v-for="semT in ficha.totales.porSemana" :key="'to-'+semT.semanaNumero"><strong>{{ formatImporteCorto(semT.objetivoSemana) }}</strong></td>
                  <td class="col-num col-sep-left"><strong>{{ formatImporteCorto(ficha.totales.objetivoMensualTotal) }}</strong></td>
                </tr>
                <!-- OBJ.SEM -->
                <tr class="fila-obj-sem">
                  <td class="col-sticky col-codigo">OBJ.SEM</td>
                  <template v-for="(semT, idx) in ficha.totales.porSemana">
                    <td class="col-multi col-sep-left" :key="'os-'+semT.semanaNumero">{{ formatImporteCorto(semT.objetivoSemana) }}</td>
                    <template v-if="mostrarDetalle">
                      <td class="col-det" :key="'osd1-'+idx"></td>
                      <td class="col-det" :key="'osd2-'+idx"></td>
                      <td class="col-det" :key="'osd3-'+idx"></td>
                    </template>
                  </template>
                  <td class="col-res col-sep-left">{{ formatImporteCorto(ficha.totales.objetivoMensualTotal) }}</td>
                  <td colspan="5"></td>
                  <td colspan="5" class="col-sep-left"></td>
                  <td class="col-dia col-sep-left" v-for="sem in ficha.calendario.semanas" :key="'od-'+sem.numero"></td>
                  <td class="col-sep-left"></td><td></td><td></td><td></td><td></td>
                  <td class="col-sep-left" v-for="sem in ficha.calendario.semanas" :key="'oo-'+sem.numero"></td>
                  <td class="col-sep-left"></td>
                </tr>
                <!-- FALTA -->
                <tr class="fila-dif-sem">
                  <td class="col-sticky col-codigo">FALTA</td>
                  <template v-for="(semT, idx) in ficha.totales.porSemana">
                    <td class="col-multi col-sep-left" :key="'fs-'+semT.semanaNumero"
                        :class="semT.diferencia >= 0 ? 'valor-positivo' : 'valor-negativo'">
                      {{ formatImporteConSigno(semT.diferencia) }}
                    </td>
                    <template v-if="mostrarDetalle">
                      <td class="col-det" :key="'fsd1-'+idx"></td>
                      <td class="col-det" :key="'fsd2-'+idx"></td>
                      <td class="col-det" :key="'fsd3-'+idx"></td>
                    </template>
                  </template>
                  <td class="col-res col-sep-left" :class="ficha.totales.faltaTotal >= 0 ? 'valor-positivo' : 'valor-negativo'">
                    {{ formatImporteConSigno(ficha.totales.faltaTotal) }}
                  </td>
                  <td colspan="5"></td>
                  <td colspan="5" class="col-sep-left"></td>
                  <td class="col-dia col-sep-left" v-for="sem in ficha.calendario.semanas" :key="'fd-'+sem.numero"></td>
                  <td class="col-sep-left"></td><td></td><td></td><td></td><td></td>
                  <td class="col-sep-left" v-for="sem in ficha.calendario.semanas" :key="'fo-'+sem.numero"></td>
                  <td class="col-sep-left"></td>
                </tr>
                <!-- % OBJ -->
                <tr class="fila-pct-sem">
                  <td class="col-sticky col-codigo">% OBJ</td>
                  <template v-for="(semT, idx) in ficha.totales.porSemana">
                    <td class="col-multi col-sep-left" :key="'ps-'+semT.semanaNumero">
                      <span class="badge-estado" :class="'estado-' + calcularEstadoColor(semT.porcentaje)">{{ formatPorcentaje(semT.porcentaje) }}</span>
                    </td>
                    <template v-if="mostrarDetalle">
                      <td class="col-det" :key="'psd1-'+idx"></td>
                      <td class="col-det" :key="'psd2-'+idx"></td>
                      <td class="col-det" :key="'psd3-'+idx"></td>
                    </template>
                  </template>
                  <td class="col-pct col-sep-left">
                    <span class="badge-estado" :class="'estado-' + calcularEstadoColor(ficha.totales.porcentajeGlobal)">{{ formatPorcentaje(ficha.totales.porcentajeGlobal) }}</span>
                  </td>
                  <td colspan="5"></td>
                  <td colspan="5" class="col-sep-left"></td>
                  <td class="col-dia col-sep-left" v-for="sem in ficha.calendario.semanas" :key="'pd-'+sem.numero"></td>
                  <td class="col-sep-left"></td><td></td><td></td><td></td><td></td>
                  <td class="col-sep-left" v-for="sem in ficha.calendario.semanas" :key="'po-'+sem.numero"></td>
                  <td class="col-sep-left"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- LEYENDA ESTADOS -->
        <div class="leyenda" v-if="ficha">
          <span class="leyenda-item"><span class="dot estado-MUY_BUENO"></span> Muy bueno &gt;105% <info-icon dominio="oeb" clave="OEB_EstadoCumplimiento" criticidad="CRITICO" pantalla="control-cifras-oeb-planificacion" /></span>
          <span class="leyenda-item"><span class="dot estado-BUENO"></span> Bueno 100-105%</span>
          <span class="leyenda-item"><span class="dot estado-MALO"></span> Malo 95-100%</span>
          <span class="leyenda-item"><span class="dot estado-MUY_MALO"></span> Muy malo &lt;95%</span>
        </div>

        <!-- LA CARGA NO HA IDO BIEN -->
        <!-- #46 · Una tabla vacía en un cuadro de mandos se lee como «no hay nada», y eso es peor
             que un error. Si la carga falla, se pasa del plazo o llega sin ficha que pintar, se
             dice, y el aviso lleva el Reintentar que ahorra salir y volver a entrar en la
             pantalla —que es lo que se hacía cambiando de mes y volviendo—. -->
        <f7-block v-if="errorCarga && !cargando" class="text-align-center aviso-carga">
          <p class="aviso-carga-texto">
            &#9888; {{ errorCarga === 'TIEMPO'
              ? 'La ficha de ' + nombreMes + ' ' + anio + ' ha tardado demasiado y no se ha podido cargar.'
              : 'La ficha de ' + nombreMes + ' ' + anio + ' ha dado un error al cargarse.' }}
            <br>No quiere decir que no haya datos: v&uacute;elvelo a intentar.
          </p>
          <f7-button fill color="blue" @click="cargarFicha">Reintentar</f7-button>
        </f7-block>

        <!-- SIN FICHA -->
        <f7-block v-if="!ficha && !cargando && !errorCarga" class="text-align-center">
          <p>No hay ficha para {{ nombreMes }} {{ anio }}</p>
          <f7-button fill color="green" @click="crearFichaMes" v-roles="['ADMIN','DIRECTOR','DTYCAL']">Crear ficha de {{ nombreMes }} {{ anio }}</f7-button>
        </f7-block>

        <!-- CARGANDO -->
        <f7-block v-if="cargando" class="text-align-center">
          <f7-preloader></f7-preloader>
          <p>Cargando datos...</p>
        </f7-block>
      </div>
    </f7-page-content>

    <!-- POPUP DETALLE PERSONA -->
    <!-- #166 · data-popup-enhanced: el enhancer global de popups (src/js/app.js) inyecta en todo
         popup sin navbar una barra con el título «Popup» y una X propia. Marcándolo como ya
         tratado no llega a crearla, así que la barra no se pinta y queda una sola X. -->
    <f7-popup :opened="popupPersona" @popup:closed="popupPersona = false" class="popup-persona" data-popup-enhanced="true">
      <f7-page v-if="personaSel" :page-content="true" :no-navbar="true">
        <!-- #166 · analisis-hdr-persona: modificador propio para no tocar el .analisis-hdr que
             comparten los otros cinco popups de la pantalla. -->
        <div class="analisis-hdr analisis-hdr-persona">
          <div class="analisis-hdr-txt">
            <div class="analisis-titulo">{{ personaSel.codigoPersonal }}</div>
            <div class="analisis-sub">Objetivo y d&iacute;as h&aacute;biles</div>
          </div>
          <div class="analisis-hdr-acciones">
            <!-- C5: ficha de desempeño (fase 1 solo Administración) -->
            <button
              v-roles="['ADMIN','DIRECTOR','DTYCAL']"
              class="btn-desempeno"
              @click="abrirDesempeno(personaSel)"
            >Desempe&ntilde;o</button>
            <button class="analisis-close" @click="popupPersona = false">&times;</button>
          </div>
        </div>
        <f7-block-title>Objetivo diario</f7-block-title>
        <f7-block strong>
          <div style="display:flex;align-items:center;gap:8px">
            <input type="number" v-model.number="objetivoEditable" step="1" min="0" style="width:100px;padding:6px 8px;border:1px solid #ddd;border-radius:6px;font-size:15px;font-weight:500;text-align:right" />
            <span style="font-size:13px;color:#888">&euro;/d&iacute;a</span>
            <button style="padding:6px 14px;background:#0F6E56;color:white;border:none;border-radius:6px;font-size:12px;cursor:pointer" @click="guardarObjetivo(personaSel)">Guardar</button>
          </div>
          <div style="margin-top:6px;font-size:12px;color:#888">Objetivo mensual: <strong>{{ formatImporte(calcularObjMensual()) }}</strong></div>
        </f7-block>
        <f7-block-title>D&iacute;as h&aacute;biles por semana</f7-block-title>
        <f7-block strong>
          <!-- #166 · Si las seis columnas no caben, el que se desplaza es este envoltorio y no el
               popup entero, que es lo que sacaba la barra horizontal en el borde de abajo. -->
          <div class="analisis-tabla-wrap">
          <table style="width:100%;border-collapse:collapse;font-size:12px">
            <thead>
              <tr>
                <th style="padding:4px;text-align:left;font-size:11px;color:#888;border-bottom:1px solid #ddd">Semana</th>
                <th style="padding:4px;text-align:center;font-size:11px;color:#888;border-bottom:1px solid #ddd">D&iacute;as</th>
                <th style="padding:4px;text-align:center;font-size:11px;color:#888;border-bottom:1px solid #ddd">Orig.</th>
                <th style="padding:4px;text-align:right;font-size:11px;color:#888;border-bottom:1px solid #ddd">Obj.sem</th>
                <th style="padding:4px;text-align:center;font-size:11px;color:#888;border-bottom:1px solid #ddd">Estado</th>
                <th style="padding:4px;border-bottom:1px solid #ddd"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(sem, idx) in personaSel.semanas" :key="'pd-sem-'+sem.semanaNumero"
                  :class="{'fila-ausencia': (sem.diasReales != null ? sem.diasReales : sem.diasHabiles) < sem.diasHabilesOriginal}"
                  style="border-bottom:1px solid #f0f0f0">
                <td style="padding:4px"><strong>S{{ sem.semanaNumero }}</strong> <span style="font-size:10px;color:#aaa">{{ obtenerRangoSemana(sem.semanaNumero) }}</span></td>
                <td style="padding:4px;text-align:center"><strong>{{ sem.diasReales != null ? sem.diasReales : sem.diasHabiles }}</strong></td>
                <td style="padding:4px;text-align:center;color:#aaa">{{ sem.diasHabilesOriginal }}</td>
                <td style="padding:4px;text-align:right">{{ formatImporteCorto(personaSel.objetivosSemana && personaSel.objetivosSemana[idx] != null ? personaSel.objetivosSemana[idx] : personaSel.objetivoDiarioActual * (sem.diasReales != null ? sem.diasReales : sem.diasHabiles)) }}</td>
                <td style="padding:4px;text-align:center">
                  <span v-if="sem.modificado" style="background:#FAEEDA;color:#633806;font-size:10px;padding:2px 6px;border-radius:8px;cursor:pointer" @click="verNotaDias(sem)">Modificado</span>
                  <span v-else-if="(sem.diasReales != null ? sem.diasReales : sem.diasHabiles) < sem.diasHabilesOriginal" style="background:#FFF3CD;color:#856404;font-size:10px;padding:2px 6px;border-radius:8px">Ausencia</span>
                  <span v-else style="color:#aaa;font-size:10px">OK</span>
                </td>
                <td style="padding:4px"><button style="padding:2px 8px;border:1px solid #ddd;border-radius:4px;background:white;font-size:10px;cursor:pointer" @click="editarDiaSemana(sem)">Editar</button></td>
              </tr>
            </tbody>
          </table>
          </div>
        </f7-block>
        <f7-block-title>Resumen del mes</f7-block-title>
        <f7-block strong>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
            <div><span style="font-size:11px;color:#888;display:block">Progresi&oacute;n</span><span style="font-size:16px;font-weight:500">{{ formatImporte(personaSel.progresionMes) }}</span></div>
            <div><span style="font-size:11px;color:#888;display:block">Objetivo</span><span style="font-size:16px;font-weight:500">{{ formatImporte(personaSel.objetivoMensual) }}</span></div>
            <div><span style="font-size:11px;color:#888;display:block">Falta</span><span style="font-size:16px;font-weight:500" :class="personaSel.faltaParaLlegar>=0?'valor-positivo':'valor-negativo'">{{ formatImporteConSigno(personaSel.faltaParaLlegar) }}</span></div>
            <div><span style="font-size:11px;color:#888;display:block">Cumplimiento</span><span class="badge-estado" :class="'estado-'+personaSel.estadoColor">{{ formatPorcentaje(personaSel.porcentajeCumplimiento) }}</span></div>
          </div>
        </f7-block>
        <template v-if="ausencias[personaSel.codigoPersonal] && ausencias[personaSel.codigoPersonal].length">
          <f7-block-title>Ausencias detectadas</f7-block-title>
          <f7-block strong style="background:#FFFDE7">
            <!-- #166 · Igual que la de semanas: el motivo de una ausencia puede ser largo. -->
            <div class="analisis-tabla-wrap">
            <table style="width:100%;border-collapse:collapse;font-size:12px">
              <thead>
                <tr>
                  <th style="padding:4px;text-align:left;font-size:11px;color:#888;border-bottom:1px solid #e0d8a8">Semana</th>
                  <th style="padding:4px;text-align:left;font-size:11px;color:#888;border-bottom:1px solid #e0d8a8">Fecha</th>
                  <th style="padding:4px;text-align:left;font-size:11px;color:#888;border-bottom:1px solid #e0d8a8">Motivo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(aus, ai) in ausencias[personaSel.codigoPersonal]" :key="'aus-'+ai" style="border-bottom:1px solid #f0eca0">
                  <td style="padding:4px"><strong>S{{ getSemanaParaDia(aus.dia) }}</strong></td>
                  <td style="padding:4px">{{ diaSemanaLabel(aus.dia) }} {{ aus.dia }}/{{ mes }}/{{ anio }}</td>
                  <td style="padding:4px;color:#e6a817;font-weight:600">{{ aus.motivo }}</td>
                </tr>
              </tbody>
            </table>
            </div>
          </f7-block>
        </template>
        <!-- #34 · Mismo rol que + Persona. No borra sus cifras del mes: deja de verse. -->
        <f7-block><f7-button fill color="red" @click="quitarPersona(personaSel)" v-roles="['ADMIN','DIRECTOR']">Quitar del mes</f7-button></f7-block>
      </f7-page>
    </f7-popup>

    <!-- POPUP ENVIAR REPORTE -->
    <!-- C5: ficha de desempeño del inspector (drill-down; datos del endpoint + Producción del cockpit) -->
    <f7-popup :opened="popupDesempeno" @popup:closed="popupDesempeno = false" class="popup-desempeno">
      <f7-page v-if="personaSel" :page-content="true" :no-navbar="true">
        <div class="analisis-hdr">
          <div>
            <div class="analisis-titulo">Desempe&ntilde;o &middot; {{ personaSel.codigoPersonal }}</div>
            <div class="analisis-sub">{{ mes }}/{{ anio }}</div>
          </div>
          <button class="analisis-close" @click="popupDesempeno = false">&times;</button>
        </div>

        <!-- Bloque PRODUCCIÓN: REUTILIZA el objeto persona del cockpit (motor OEB, sin llamada nueva) -->
        <f7-block-title>Producci&oacute;n (motor OEB)</f7-block-title>
        <f7-block strong class="desemp-grid">
          <div><span class="desemp-lbl">Objetivo mes</span><span class="desemp-val">{{ formatImporte(personaSel.objetivoMensual) }}</span></div>
          <div><span class="desemp-lbl">Ejecutado (OEB-R + TPC S&Iacute;)</span><span class="desemp-val">{{ formatImporte(sumaDesglosePersona(personaSel.codigoPersonal, 'oebR') + sumaTpcPersona(personaSel.codigoPersonal)) }}</span></div>
          <div><span class="desemp-lbl">Pdte. sincronizar (OEB-P)</span><span class="desemp-val">{{ formatImporte(sumaDesglosePersona(personaSel.codigoPersonal, 'oebP') + sumaDesglosePersona(personaSel.codigoPersonal, 'oebPPend')) }}</span></div>
          <div><span class="desemp-lbl">OEB total (progresi&oacute;n)</span><span class="desemp-val">{{ formatImporte(personaSel.progresionMes) }}</span></div>
        </f7-block>

        <template v-if="desempenoLoading">
          <f7-block class="text-align-center"><f7-preloader></f7-preloader></f7-block>
        </template>
        <template v-else-if="desempeno">
          <f7-block-title>Informes (TPI)</f7-block-title>
          <f7-block strong class="desemp-grid">
            <div><span class="desemp-lbl">TPIP&euro; (planificado)</span><span class="desemp-val">{{ formatImporte(desempeno.informes.tpipEuros) }}</span></div>
            <div><span class="desemp-lbl">TPIR&euro; (realizado)</span><span class="desemp-val">{{ formatImporte(desempeno.informes.tpirEuros) }}</span></div>
            <div v-if="desempeno.informes.pctRealizadoVsPlanificado != null"><span class="desemp-lbl">% realizado</span><span class="desemp-val">{{ Math.round(desempeno.informes.pctRealizadoVsPlanificado * 100) + '%' }}</span></div>
            <div><span class="desemp-lbl">% en plazo</span><span class="desemp-val">{{ desempeno.informes.pctEnPlazo != null ? Math.round(desempeno.informes.pctEnPlazo * 100) + '%' : '—' }}</span></div>
          </f7-block>

          <f7-block-title>Estados del expediente (situaci&oacute;n actual)</f7-block-title>
          <f7-block strong class="desemp-grid">
            <div><span class="desemp-lbl">Pdte. firma (hoy)</span><span class="desemp-val">{{ desempeno.estados.pendienteFirma }}</span></div>
            <div><span class="desemp-lbl">Pdte. confirmar (hoy)</span><span class="desemp-val">{{ desempeno.estados.pendienteConfirmar }}</span></div>
            <div><span class="desemp-lbl">Errores auditor&iacute;a (hoy)</span><span class="desemp-val">{{ desempeno.estados.erroresAuditoria }}</span></div>
            <div><span class="desemp-lbl">Enviados (mes)</span><span class="desemp-val">{{ desempeno.estados.enviados }}</span></div>
          </f7-block>

          <f7-block-title>Calidad (firmas)</f7-block-title>
          <f7-block strong class="desemp-grid">
            <div><span class="desemp-lbl">Ratio firmas</span><span class="desemp-val">{{ desempeno.calidad.ratioFirmas != null ? desempeno.calidad.ratioFirmas.toFixed(2) : '—' }}</span></div>
            <div><span class="desemp-lbl">Firmas periodo</span><span class="desemp-val">{{ desempeno.calidad.totalFirmas }}</span></div>
            <div><span class="desemp-lbl">Refirmados</span><span class="desemp-val">{{ desempeno.calidad.nRefirmados }}</span></div>
            <div><span class="desemp-lbl">Errores auditor&iacute;a</span><span class="desemp-val">{{ desempeno.calidad.nErroresAuditoria }}</span></div>
          </f7-block>

          <f7-block-title>Ruta (real moviloc vs te&oacute;rico)</f7-block-title>
          <f7-block strong class="desemp-grid">
            <div><span class="desemp-lbl">Eficiencia</span><span class="desemp-val">{{ desempeno.ruta.eficienciaPct != null ? (desempeno.ruta.eficienciaPct * 100).toFixed(1) + '%' : '—' }}</span></div>
            <div><span class="desemp-lbl">Km real</span><span class="desemp-val">{{ desempeno.ruta.kmReal != null ? desempeno.ruta.kmReal + ' km' : '—' }}</span></div>
            <div><span class="desemp-lbl">T. desplazamiento</span><span class="desemp-val">{{ desempeno.ruta.tdMin != null ? desempeno.ruta.tdMin + ' min' : '—' }}</span></div>
            <div><span class="desemp-lbl">Tramos</span><span class="desemp-val">{{ desempeno.ruta.nTramos }}</span></div>
          </f7-block>

          <f7-block-title>Tendencia 6 meses (cumplimiento %)</f7-block-title>
          <f7-block strong>
            <div class="tendencia-barras">
              <div v-for="t in desempeno.tendencia6m" :key="'t6m-' + t.anio + '-' + t.mes" class="tendencia-col">
                <div class="tendencia-barra-wrap">
                  <div
                    class="tendencia-barra"
                    :class="{ 'tendencia-sindato': t.sinDato }"
                    :style="t.sinDato ? 'height:100%' : ('height:' + Math.min(100, Math.max(4, t.cumplimientoPct)) + '%')"
                    v-tooltip="t.sinDato ? 'Sin dato fiable' : (t.cumplimientoPct.toFixed(1) + ' %')"
                  ></div>
                </div>
                <div class="tendencia-mes">{{ t.mes }}/{{ String(t.anio).slice(2) }}</div>
                <div class="tendencia-pct">{{ t.sinDato ? '—' : Math.round(t.cumplimientoPct) + '%' }}</div>
              </div>
            </div>
          </f7-block>

          <f7-block-title v-if="desempeno.informesPendientes && desempeno.informesPendientes.length">
            Informes pendientes ({{ desempeno.informesPendientes.length }})
          </f7-block-title>
          <f7-list v-if="desempeno.informesPendientes && desempeno.informesPendientes.length" media-list>
            <f7-list-item
              v-for="(inf, idx) in desempeno.informesPendientes"
              :key="'desemp-inf-' + idx"
              :title="(inf.codigoExpediente || '—') + (inf.cliente ? ' · ' + inf.cliente : '')"
              :subtitle="(inf.servicio || '—') + ' · ' + (inf.estadoExpediente || '')"
              :link="inf.idExpediente ? '/expedientes/view/' + inf.idExpediente + '/' : null"
            ></f7-list-item>
          </f7-list>

          <f7-block v-if="desempeno.marcas && desempeno.marcas.length" class="desemp-marcas">
            <div v-for="(m, idx) in desempeno.marcas" :key="'marca-' + idx">&#9888; {{ m }}</div>
          </f7-block>
        </template>
      </f7-page>
    </f7-popup>

    <f7-popup :opened="popupReporte" @popup:closed="popupReporte = false" class="popup-reporte">
      <f7-page>
        <f7-navbar title="Enviar informe OEB Planificaci&oacute;n">
          <f7-nav-right><f7-link popup-close icon-f7="xmark"></f7-link></f7-nav-right>
        </f7-navbar>
        <f7-page-content>
          <f7-block-title>Tipo de informe</f7-block-title>
          <f7-block strong>
            <div style="display:flex;gap:8px">
              <div @click="reporteTipo = 'DIARIO'; actualizarAsuntoReporte()" style="flex:1;padding:10px;text-align:center;border-radius:8px;cursor:pointer;font-size:12px;font-weight:600" :style="reporteTipo === 'DIARIO' ? {border:'2px solid #1a2744',background:'rgba(26,39,68,0.05)',color:'#1a2744'} : {border:'1px solid #ddd',color:'#64748b'}">
                <div>Diario</div><div style="font-size:10px;font-weight:400;margin-top:2px">Hoy + acumulado</div>
              </div>
              <div @click="reporteTipo = 'SEMANAL'; actualizarAsuntoReporte()" style="flex:1;padding:10px;text-align:center;border-radius:8px;cursor:pointer;font-size:12px;font-weight:600" :style="reporteTipo === 'SEMANAL' ? {border:'2px solid #1a2744',background:'rgba(26,39,68,0.05)',color:'#1a2744'} : {border:'1px solid #ddd',color:'#64748b'}">
                <div>Semanal</div><div style="font-size:10px;font-weight:400;margin-top:2px">Semana en curso</div>
              </div>
              <div @click="reporteTipo = 'MENSUAL'; actualizarAsuntoReporte()" style="flex:1;padding:10px;text-align:center;border-radius:8px;cursor:pointer;font-size:12px;font-weight:600" :style="reporteTipo === 'MENSUAL' ? {border:'2px solid #1a2744',background:'rgba(26,39,68,0.05)',color:'#1a2744'} : {border:'1px solid #ddd',color:'#64748b'}">
                <div>Mensual</div><div style="font-size:10px;font-weight:400;margin-top:2px">Mes completo</div>
              </div>
            </div>
          </f7-block>
          <f7-block-title>Destinatarios</f7-block-title>
          <f7-block strong>
            <div style="margin-bottom:8px">
              <label style="font-size:11px;font-weight:600;color:#64748b;display:block;margin-bottom:4px">Para (separados por coma):</label>
              <textarea v-model="reporteEmails" rows="2" style="width:100%;padding:6px 8px;border:1px solid #ddd;border-radius:4px;font-size:12px;resize:vertical;box-sizing:border-box" placeholder="email1@empresa.com, email2@empresa.com"></textarea>
            </div>
            <div style="margin-bottom:8px">
              <label style="font-size:11px;font-weight:600;color:#64748b;display:block;margin-bottom:4px">CC (opcional):</label>
              <input v-model="reporteCc" type="text" style="width:100%;padding:6px 8px;border:1px solid #ddd;border-radius:4px;font-size:12px;box-sizing:border-box" placeholder="cc@empresa.com">
            </div>
          </f7-block>
          <f7-block-title>Asunto</f7-block-title>
          <f7-block strong>
            <input v-model="reporteAsunto" type="text" style="width:100%;padding:6px 8px;border:1px solid #ddd;border-radius:4px;font-size:12px;box-sizing:border-box">
          </f7-block>
          <!-- Desde la #15 el envio recalcula los datos antes de generar el correo, asi que puede
               tardar hasta un minuto. Se dice en el boton: si solo pusiera "Enviando..." pareceria
               colgado y quien lo mira acabaria volviendo a pulsar. -->
          <f7-block>
            <f7-button fill color="green" @click="confirmarEnvioReporte" :disabled="enviandoReporte">
              {{ enviandoReporte ? 'Recalculando y enviando...' : 'Enviar informe' }}
            </f7-button>
          </f7-block>
        </f7-page-content>
      </f7-page>
    </f7-popup>

    <!-- POPUP DISPONIBILIDAD HORAS -->
    <f7-popup :opened="popupDisp" @popup:closed="popupDisp = false" class="popup-disp">
      <f7-page :page-content="true" v-if="dispPersona" :no-navbar="true">
        <div class="analisis-hdr">
          <div>
            <div class="analisis-titulo">Disponibilidad &mdash; {{ dispPersona.codigoPersonal }}</div>
          </div>
          <button class="analisis-close" @click="popupDisp = false">&times;</button>
        </div>
        <div style="padding:0 12px">
          <div style="text-align:center;padding:8px 0">
            <f7-link @click="dispSemPrev" icon-f7="chevron_left" style="padding:0 12px"></f7-link>
            <strong style="font-size:14px">S{{ dispSemNum }}</strong>
            <span style="font-size:12px;color:#888;margin-left:4px">({{ dispSemRango }})</span>
            <f7-link @click="dispSemNext" icon-f7="chevron_right" style="padding:0 12px"></f7-link>
          </div>
          <div style="padding:0 12px">
            <table class="tabla-disp">
              <thead>
                <tr><th style="text-align:left">D&iacute;a</th><th style="text-align:left">Fecha</th><th style="text-align:right">Planificado</th><th style="text-align:right">Disponible</th></tr>
              </thead>
              <tbody>
                <tr v-for="d in dispFilas" :key="d.dia" :class="d.dispClase">
                  <td style="text-align:left;font-weight:600">{{ d.nombreDia }}</td>
                  <td style="text-align:left">{{ d.dia }} {{ nombreMesCorto }} <span v-if="d.motivo" style="color:#e6a817;font-size:10px;font-weight:600">{{ d.motivo }}</span></td>
                  <td style="text-align:right">{{ formatMinHM(d.minutos) }}</td>
                  <td style="text-align:right;font-weight:600">{{ d.motivo ? '—' : formatMinHMSigned(d.disponible) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="fila-total-disp">
                  <td colspan="2" style="text-align:left"><strong>Total</strong></td>
                  <td style="text-align:right"><strong>{{ formatMinHM(dispTotalPlan) }}</strong></td>
                  <td style="text-align:right"><strong>{{ formatMinHMSigned(dispTotalDisp) }}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </f7-page>
    </f7-popup>

    <!-- POPUP ANÁLISIS DIFERENCIAS -->
    <f7-popup :opened="popupAnalisis" @popup:closed="popupAnalisis = false" class="popup-analisis">
      <f7-page :page-content="true" :no-navbar="true">
        <div class="analisis-hdr">
          <div>
            <div class="analisis-titulo">An&aacute;lisis diferencias &mdash; {{ analisisPersonaCod }}</div>
            <div class="analisis-sub">Semana S{{ analisisSemActual ? analisisSemActual.semana : '' }} &middot; {{ nombreMes }} {{ anio }}</div>
          </div>
          <div style="display:flex;gap:6px;align-items:center">
            <button class="analisis-nav" @click="analisisSemPrev">&lt;</button>
            <button class="analisis-nav" @click="analisisSemNext">&gt;</button>
            <button class="analisis-close" @click="popupAnalisis = false">&times;</button>
          </div>
        </div>
        <!-- #16 · El popup tapa el sello de la pantalla y sus datos no son los de la ficha: el
             analisis se calcula aparte y luego se reutiliza (cache de sendRequest y analisisCache),
             asi que lleva su propio sello con la marca que publica el endpoint. -->
        <div class="sello-datos-bar sello-datos-bar--popup" v-if="analisisData">
          <sello-datos estado="fecha" :fecha="analisisData.fechaCalculo" />
        </div>

        <div style="padding:0 12px" v-if="analisisSemActual">
          <!-- RESUMEN KPIs — OEB y TPC calculados por el endpoint (desglose completo, cuadra con items) -->
          <div class="analisis-kpis">
            <div class="analisis-kpi"><div class="analisis-kpi-label">OEB calendario</div><div class="analisis-kpi-val" style="color:#1a2744">{{ formatImporte(analisisSemActual.oebSemana) }}</div></div>
            <div class="analisis-kpi"><div class="analisis-kpi-label">TPC(SI) reparto</div><div class="analisis-kpi-val" style="color:#dc3545">{{ formatImporte(analisisSemActual.tpcSemana) }}</div></div>
            <div class="analisis-kpi"><div class="analisis-kpi-label">Diferencia</div><div class="analisis-kpi-val" :class="(analisisSemActual.diferencia || 0) >= 0 ? 'valor-positivo' : 'valor-negativo'">{{ formatImporteConSigno(analisisSemActual.diferencia) }}</div></div>
          </div>

          <!-- DISTRIBUCIÓN POR CAUSA -->
          <div class="analisis-causas" v-if="analisisSemActual.items && analisisSemActual.items.length">
            <span v-for="c in analisisCausasResumen" :key="c.causa" class="causa-badge" :style="{ background: causaColor(c.causa), color: '#fff' }">{{ causaLabel(c.causa) }}: {{ formatImporteCorto(c.importe) }} &euro;</span>
          </div>

          <!-- TOGGLE mostrar/ocultar líneas OK -->
          <div style="padding:4px 0;text-align:right">
            <label style="font-size:11px;color:#666;cursor:pointer"><input type="checkbox" v-model="analisisMostrarOk" style="margin-right:4px" />Mostrar todas las l&iacute;neas</label>
          </div>

          <!-- TABLA ITEMS -->
          <div class="analisis-tabla-wrap" v-if="analisisItemsFiltrados && analisisItemsFiltrados.length">
            <table class="analisis-tabla">
              <thead>
                <tr>
                  <th style="text-align:left">Causa</th>
                  <th>Pedido</th>
                  <th>L&iacute;nea</th>
                  <th style="text-align:left">Servicio</th>
                  <th>OEB</th>
                  <th>TPC</th>
                  <th>% Rep.</th>
                  <th>Dif.</th>
                  <th>F.Plan</th>
                  <th>F.Ejec</th>
                  <th>Acci&oacute;n</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, ii) in analisisItemsFiltrados" :key="ii" :class="ii % 2 === 1 ? 'fila-alt' : ''">
                  <td style="text-align:left"><span class="causa-badge-sm" :style="{ background: causaColor(item.causa) }">{{ causaLabel(item.causa) }}</span></td>
                  <td>{{ item.numeroPedido || '&mdash;' }}</td>
                  <td>{{ item.numLinea != null ? item.numLinea : '&mdash;' }}</td>
                  <td style="text-align:left;max-width:150px;overflow:hidden;text-overflow:ellipsis">{{ item.servicio || '&mdash;' }}</td>
                  <td style="text-align:right">{{ formatImporteCorto(item.importeOEB) }}</td>
                  <td style="text-align:right">{{ formatImporteCorto(item.importeTPC) }}</td>
                  <td style="text-align:center" :style="item.porcentajeReparto && item.porcentajeReparto < 100 ? {color:'#e6a817',fontWeight:'700'} : {color:'#aaa'}">{{ item.porcentajeReparto ? Math.round(item.porcentajeReparto) + '%' : '100%' }}</td>
                  <td style="text-align:right" :class="item.diferencia >= 0 ? 'valor-positivo' : 'valor-negativo'">{{ formatImporteConSigno(item.diferencia) }}</td>
                  <td>{{ item.fechaPlanificacion ? item.fechaPlanificacion.substring(5) : '&mdash;' }}</td>
                  <td>{{ item.fechaEjecucion ? item.fechaEjecucion.substring(5) : '&mdash;' }}</td>
                  <td>
                    <button v-if="item.pedidoId" class="btn-ver" @click="navegarPedido(item.pedidoId)">Ver &rarr;</button>
                    <button v-else-if="item.idTarea" class="btn-ver" @click="navegarTarea(item.idTarea)">Tarea &rarr;</button>
                    <span v-else>&mdash;</span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr style="font-weight:700;border-top:2px solid #1a2744;background:#f0f4fa">
                  <td colspan="4" style="text-align:right;padding-right:8px">TOTALES</td>
                  <td style="text-align:right">{{ formatImporteCorto(analisisItemsTotalOeb) }}</td>
                  <td style="text-align:right">{{ formatImporteCorto(analisisItemsTotalTpc) }}</td>
                  <td></td>
                  <td style="text-align:right" :class="analisisItemsTotalDif >= 0 ? 'valor-positivo' : 'valor-negativo'">{{ formatImporteConSigno(analisisItemsTotalDif) }}</td>
                  <td colspan="3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div v-else style="text-align:center;padding:24px;color:#888;font-size:13px">Sin diferencias detectadas en esta semana</div>
        </div>
        <div v-else style="text-align:center;padding:40px"><f7-preloader></f7-preloader><p>Cargando an&aacute;lisis...</p></div>
      </f7-page>
    </f7-popup>

    <!-- POPUP DESGLOSE DIARIO -->
    <f7-popup :opened="popupDesglose" @popup:closed="popupDesglose = false" class="popup-desglose">
      <f7-page :page-content="true" :no-navbar="true">
        <div class="analisis-hdr">
          <div>
            <div class="analisis-titulo">Desglose diario &mdash; {{ desgloseCod }}</div>
            <div class="analisis-sub">Semana S{{ desgloseSemNum }} &middot; {{ nombreMes }} {{ anio }}</div>
          </div>
          <button class="analisis-close" @click="popupDesglose = false">&times;</button>
        </div>
        <div style="padding:0 12px" v-if="desgloseDias && desgloseDias.length">
          <table class="analisis-tabla">
            <thead>
              <tr>
                <th>D&iacute;a</th>
                <th>Fecha</th>
                <th style="text-align:right">OEB</th>
                <th style="text-align:right">OEB-R</th>
                <th style="text-align:right">OEB-P</th>
                <th style="text-align:right">OEB-PPEND</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(d, di) in desgloseDias" :key="di" :class="di % 2 === 1 ? 'fila-alt' : ''">
                <td>{{ diaSemanaLabel(d.dia) }}</td>
                <td>{{ d.dia }}/{{ mes }}/{{ anio }} <span v-if="getAusenciaDia(d.dia)" style="color:#e6a817;font-size:9px;font-weight:600">{{ getAusenciaDia(d.dia) }}</span></td>
                <td style="text-align:right">{{ formatImporteCorto(d.oeb || 0) }}</td>
                <td style="text-align:right">{{ formatImporteCorto(d.oebR || 0) }}</td>
                <td style="text-align:right">{{ formatImporteCorto(d.oebP || 0) }}</td>
                <td style="text-align:right">{{ formatImporteCorto(d.oebPPend || 0) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr style="font-weight:700;border-top:2px solid #1a2744;background:#f0f4fa">
                <td colspan="2" style="text-align:right">TOTALES</td>
                <td style="text-align:right">{{ formatImporteCorto(desgloseDias.reduce(function(s,d){ return s+(d.oeb||0); }, 0)) }}</td>
                <td style="text-align:right">{{ formatImporteCorto(desgloseDias.reduce(function(s,d){ return s+(d.oebR||0); }, 0)) }}</td>
                <td style="text-align:right">{{ formatImporteCorto(desgloseDias.reduce(function(s,d){ return s+(d.oebP||0); }, 0)) }}</td>
                <td style="text-align:right">{{ formatImporteCorto(desgloseDias.reduce(function(s,d){ return s+(d.oebPPend||0); }, 0)) }}</td>
              </tr>
            </tfoot>
          </table>
          <!-- RESUMEN HORAS -->
          <div style="margin-top:10px;padding:8px 12px;background:#f8fafe;border-radius:6px;font-size:12px">
            <div style="display:flex;justify-content:space-between;padding:2px 0"><span>Horas semana ({{ desgloseDisponDiasHab }} d&iacute;as h&aacute;biles &times; 8h)</span><strong>{{ formatMinHM(desgloseDisponDiasHab * 480) }}</strong></div>
            <div style="display:flex;justify-content:space-between;padding:2px 0"><span>Horas ocupadas (planificadas)</span><strong>{{ formatMinHM(desgloseMinutosOcupados) }}</strong></div>
            <div style="display:flex;justify-content:space-between;padding:2px 0;color:#198754;font-weight:700"><span>Horas disponibles</span><strong>{{ formatMinHM(Math.max(0, desgloseDisponDiasHab * 480 - desgloseMinutosOcupados)) }}</strong></div>
          </div>
        </div>
        <div v-else style="text-align:center;padding:24px;color:#888;font-size:13px">Sin datos para esta semana</div>
      </f7-page>
    </f7-popup>

    <!-- MODAL OEB MANUAL OVERRIDE (div simple, no F7 popup — evita bloqueo de backdrop) -->
    <div v-if="popupManual" class="modal-manual-backdrop" @click.self="popupManual = false">
      <div class="modal-manual">
        <div class="analisis-hdr" style="border-radius:8px 8px 0 0">
          <div>
            <div class="analisis-titulo">OEB Manual &mdash; {{ manualCod }}</div>
            <div class="analisis-sub">Semana S{{ manualSemNum }} &middot; {{ nombreMes }} {{ anio }}</div>
          </div>
          <button class="analisis-close" @click="popupManual = false">&times;</button>
        </div>
        <div style="padding:16px">
          <div style="margin-bottom:12px">
            <label style="font-size:12px;color:#666;display:block;margin-bottom:4px">Importe OEB manual</label>
            <input type="number" v-model="manualValor" step="0.01" style="width:100%;padding:8px;border:1px solid #ddd;border-radius:6px;font-size:14px" placeholder="Cumplimentar manualmente" />
          </div>
          <div style="margin-bottom:12px">
            <label style="font-size:12px;color:#666;display:block;margin-bottom:4px">Motivo del ajuste manual <span style="color:#dc3545">*</span></label>
            <textarea v-model="manualMotivo" maxlength="500" rows="3" style="width:100%;padding:8px;border:1px solid #ddd;border-radius:6px;font-size:13px;resize:vertical;font-family:inherit" placeholder="Explica por qué se corrige manualmente (auditoría ENAC UNE-EN 17020)"></textarea>
            <div style="font-size:10px;color:#888;margin-top:2px">Obligatorio para auditoría. Máx. 500 caracteres.</div>
            <div v-if="manualError" style="font-size:11px;color:#dc3545;margin-top:4px"><strong>{{ manualError }}</strong></div>
          </div>
          <div v-if="manualInfo" style="font-size:11px;color:#888;margin-bottom:12px">
            Establecido por <strong>{{ manualInfo.usuario }}</strong> el {{ formatFechaCorta(manualInfo.fecha) }}
            <div v-if="manualInfo.motivo" style="margin-top:4px">Motivo registrado: <em>{{ manualInfo.motivo }}</em></div>
            <div v-else style="margin-top:4px;color:#aaa">Motivo registrado: &mdash; (registro antiguo sin motivo)</div>
          </div>
          <div style="display:flex;gap:8px">
            <button style="flex:1;padding:8px;background:#1a2744;color:#fff;border:none;border-radius:6px;font-size:13px;cursor:pointer" @click="guardarManualOverride">Guardar</button>
            <button v-if="manualInfo" style="flex:1;padding:8px;background:#dc3545;color:#fff;border:none;border-radius:6px;font-size:13px;cursor:pointer" @click="borrarManualOverride">Borrar manual</button>
          </div>
        </div>
      </div>
    </div>

    <!-- POPUP GUÍA DE USO -->
    <f7-popup :opened="popupInfo" @popup:close="fullscreenInfo = false; popupInfo = false" class="ct-popup-guia" :style="fullscreenInfo ? '--f7-popup-tablet-width:95vw;--f7-popup-tablet-height:95vh' : ''">
      <div style="display:flex;flex-direction:column;height:100%;">
        <div class="ct-hdr" style="flex-shrink:0;background:linear-gradient(135deg,#1a2744,#2d5f8a)">
          <div class="ct-hdr-top">
            <div class="ct-hdr-left">
              <span class="ct-hdr-ico">&#128200;</span>
              <div><div class="ct-hdr-title">Gu&iacute;a de uso</div><div class="ct-hdr-sub">PLANIFICACI&Oacute;N OEB</div></div>
            </div>
            <div class="ct-hdr-right">
              <span class="ct-hdr-badge">{{ instFiltradas.length }} instrucciones</span>
              <button class="ct-hdr-add" @click="nuevaInst">+</button>
              <button class="ct-hdr-close" @click="fullscreenInfo = !fullscreenInfo">{{ fullscreenInfo ? '&#8662;' : '&#9974;' }}</button>
              <button class="ct-hdr-close" @click="fullscreenInfo = false; popupInfo = false">&#10005;</button>
            </div>
          </div>
          <div class="ct-search-wrap">
            <span class="ct-search-ico">&#128269;</span>
            <input class="ct-search" type="text" placeholder="Buscar instrucciones..." v-model="busquedaInst">
            <button v-if="busquedaInst" class="ct-search-x" @click="busquedaInst = ''">&#10005;</button>
          </div>
        </div>
        <div class="ct-body" style="flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;">
          <!-- #85 · Quién sale en «+ Persona» y quién pasa al mes nuevo, con las exclusiones que
               quedan a propósito. Va fijo en la pantalla y no como instrucción de base de datos
               porque describe lo que hace el código: si se pudiera editar o borrar, mañana diría
               otra cosa que la de la regla que se aplica de verdad. -->
          <div class="ct-card ct-card-fija" style="border-left-color:#0F6E56">
            <div class="ct-card-hdr">
              <span class="ct-card-num" style="background:#0F6E56">i</span>
              <div class="ct-card-title-area"><div class="ct-card-title">Quién aparece en este mes y quién no</div></div>
            </div>
            <div class="ct-card-body">
              <div class="ct-card-content">
                <p><b>Regla única:</b> toda persona con el check Activo marcado en Configuración OEB del año de este mes puede añadirse a este mes, y al crear un mes nuevo pasan todas las del mes anterior que sigan activas en esa configuración.</p>
                <p><b>«+ Persona»</b> ofrece a todas las personas activas en la Configuración OEB del año de este mes, y solo deja fuera a las que ya se ven aquí. Quien se sacó con «Quitar del mes» sigue saliendo en la lista, marcado como «vuelve con sus cifras»: al añadirlo recupera su fila con los datos que tenía en el mes, no empieza de cero.</p>
                <p><b>«Crear ‹mes›»</b> monta el mes nuevo con las personas activas en la Configuración OEB del año de ESE mes. Los objetivos que se hubieran retocado a mano en el mes anterior se heredan; el resto sale del objetivo del año.</p>
                <p><b>Las únicas razones por las que alguien no aparece:</b></p>
                <ol>
                  <li>No tiene el check Activo marcado en Configuración OEB <b>del año de este mes</b>. Manda el año: estar activo en 2025 no mete a nadie en un mes de 2026. Y manda también sobre el trabajo del mes: si alguien tiene producción en el mes pero no está activo en la configuración del año, no entra, porque quitarle el check es una decisión y crear el mes no la deshace.</li>
                  <li>El mes anterior tenía la lista de personas guardada con «Guardar personas del mes» y esa persona no estaba en ella. El mes nuevo hereda esa lista a propósito, para no tener que repetir cada mes la decisión de quién se ve. Se resuelve añadiéndola con «+ Persona».</li>
                  <li>Ya se ve en este mes: nadie entra dos veces.</li>
                </ol>
                <p><b>Dos avisos más.</b> Si un año todavía no tiene ninguna persona activa en su Configuración OEB, se usa la lista general de personas —la que no va por años— para no dejar el mes vacío. Y el botón «Desactivar» de Configuración OEB actúa sobre esa lista general, no sobre el año: para que alguien deje de entrar en los meses de un año hay que desmarcar su check Activo en la Configuración OEB de ese año.</p>
                <p><b>Quien produce sin estar configurado.</b> Si en el trabajo del mes aparece alguien que no está en la Configuración OEB, al crear el mes se le da de alta automáticamente en la de ese año, con 12,72 € al día de objetivo provisional, y entra en el mes. No se queda invisible produciendo, y desde Configuración OEB se le puede poner su objetivo de verdad o darle de baja. A quien ya tiene el check Activo desmarcado en la configuración del año no se le da de alta: eso es una decisión tomada y crear el mes no la deshace.</p>
              </div>
            </div>
          </div>
          <div v-if="instFiltradas.length" class="ct-counter">{{ instFiltradas.length }} instrucciones</div>
          <div v-if="!instFiltradas.length" class="ct-empty"><span style="font-size:32px;opacity:.3">&#128200;</span><p>Sin instrucciones. Pulsa + para crear.</p></div>
          <div v-for="(inst, idx) in instFiltradas" :key="inst.id" class="ct-card" style="border-left-color:#1a2744"
            draggable="true" @dragstart="draggingInst = idx" @dragover.prevent @drop="dropInst(idx)">
            <div class="ct-card-hdr" @click="expandedInst === inst.id ? expandedInst = null : expandedInst = inst.id">
              <span class="ct-card-num" style="background:#1a2744">{{ idx + 1 }}</span>
              <div class="ct-card-title-area"><div class="ct-card-title">{{ inst.titulo }}</div></div>
              <span class="ct-card-chev">{{ expandedInst === inst.id ? '&#9650;' : '&#9660;' }}</span>
            </div>
            <div v-if="expandedInst === inst.id" class="ct-card-body">
              <template v-if="inst._editando">
                <div class="ct-form-field"><label>T&iacute;tulo</label><input type="text" v-model="inst.titulo"></div>
                <div class="ct-form-field"><label>Contenido</label><textarea v-model="inst.texto" rows="4"></textarea></div>
                <div class="ct-form-btns">
                  <button class="ct-fbtn ct-fbtn-ok" @click="guardarInst(inst)">Guardar</button>
                  <button class="ct-fbtn ct-fbtn-cancel" @click="inst._editando = false">Cancelar</button>
                </div>
              </template>
              <template v-else>
                <div class="ct-card-content">{{ inst.texto }}</div>
                <div class="ct-card-actions">
                  <button @click.stop="$set(inst, '_editando', true)">&#9998; Editar</button>
                  <button @click.stop="eliminarInst(inst)" style="color:#dc3545">&#128465; Eliminar</button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </f7-popup>
  </f7-page>
</template>
<style scoped>
/* Zoom container */
.contenido-plan { font-size: 12px; }

/* Selector mes */
.mes-selector { display:flex; align-items:center; justify-content:center; gap:0.8em; padding:0.3em 0.6em 0.2em; }
.select-anio, .select-mes { font-size:1.2em; font-weight:600; padding:0.2em 0.4em; border:1px solid #ddd; border-radius:0.3em; background:white; color:#1a2744; text-align:center; }
.select-anio { min-width:5em; }
.select-mes { min-width:8em; }
.mes-badge { display:inline-block; font-size:0.7em; padding:0.2em 0.8em; border-radius:2em; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; }
.badge-activo { background:#E8F5E9; color:#2E7D32; }
.badge-cerrado { background:#ECEFF1; color:#546E7A; }

/* KPIs */
.metricas-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:0.4em; padding:0 0.6em 0.3em; }
@media (max-width:600px) { .metricas-grid { grid-template-columns:repeat(2,1fr); } }
.metrica-card { background:linear-gradient(135deg,#fff 0%,#f8fafe 100%); border:1px solid #e8edf3; border-radius:0.5em; padding:0.4em 0.6em; box-shadow:0 1px 4px rgba(0,0,0,0.03); }
.metrica-label { font-size:0.65em; color:#7c8db0; text-transform:uppercase; letter-spacing:0.04em; font-weight:600; margin-bottom:0.1em; }
.metrica-valor { font-size:1.2em; font-weight:700; color:#1a2744; letter-spacing:-0.02em; }

/* Info actualización */
.info-actualizacion { padding:0 0.6em 0.2em; font-size:0.65em; color:#9aa5b8; letter-spacing:0.02em; }

/* Toolbar toggles */
.toolbar-plan { padding:0 0.6em 0.3em; }
.toggles-row { display:flex; gap:0.35em; flex-wrap:wrap; margin-bottom:0.3em; }
.acciones-row { display:flex; gap:0.35em; flex-wrap:wrap; align-items:center; }
.acciones-row .button { font-size:0.75em; font-weight:600; border-radius:0.5em; padding:0.3em 0.8em; }
.toggle-btn { display:inline-flex; align-items:center; padding:0.3em 0.7em; border-radius:0.4em; font-size:0.8em; font-weight:700; cursor:pointer; transition:all 0.15s; user-select:none; white-space:nowrap; }

/* Leyenda cifras */
.leyenda-cifras { padding:0 0.6em 0.4em; display:flex; gap:1em; flex-wrap:wrap; font-size:0.7em; color:#64748b; }
.ley-item { display:flex; align-items:center; gap:0.3em; }
.ley-dot { display:inline-block; width:0.7em; height:0.7em; border-radius:50%; }
.aviso-infpend { margin:0 0.6em 0.4em; padding:0.3em 0.6em; font-size:0.68em; color:#854F0B;
  background:#FFF3E0; border:1px solid #FFB74D; border-radius:3px; }
.aviso-carga { background:#FDECEA; border:1px solid #F5A29B; border-radius:0.6em; }
.aviso-carga-texto { color:#8A1C13; font-weight:600; }

/* Tabla principal */
.tabla-container { padding:0 0.4em; }
.tabla-scroll { overflow-x:auto; -webkit-overflow-scrolling:touch; border:1px solid #dde3ed; border-radius:0.8em; margin:0 0.6em; box-shadow:0 1px 6px rgba(0,0,0,0.04); background:white; }
.tabla-plan { border-collapse:collapse; font-size:1em; white-space:nowrap; width:100%; }

/* Cabecera */
.tabla-plan thead th { background:#1a2744; color:#c8d3e5; padding:0.5em 0.4em; font-weight:600; font-size:0.85em; text-transform:uppercase; letter-spacing:0.04em; border-bottom:none; text-align:right; position:sticky; top:0; z-index:2; }
.tabla-plan thead th.col-codigo { text-align:left; color:white; }
.tabla-plan thead th.col-multi { text-align:center; font-size:12px; }

/* Celdas cuerpo */
.tabla-plan tbody td { padding:0.3em 0.35em; border-bottom:1px solid #eef0f5; text-align:right; color:#2d3748; font-size:11px; font-weight:500; }
.tabla-plan tbody td.col-codigo { text-align:left; font-weight:700; color:#1a2744; letter-spacing:0.02em; min-width:55px; max-width:65px; }
.fila-alt { background:#fafbfd; }
.fila-persona:hover { background:#eef2f9 !important; }

/* Sticky column */
.col-sticky { position:sticky; left:0; background:white; z-index:1; box-shadow:2px 0 8px rgba(0,0,0,0.06); }
thead .col-sticky { background:#1a2744; z-index:3; }
.fila-totales .col-sticky, .fila-obj-sem .col-sticky, .fila-dif-sem .col-sticky, .fila-pct-sem .col-sticky { background:#eef1f7; }
.fila-alt .col-sticky { background:#fafbfd; }

/* Multi-value cells — columnas principales amplias */
.col-multi { min-width:140px; padding:0.2em 0.3em !important; vertical-align:middle; }
/* Centered layout (1-3 active) */
.multi-center { display:flex; flex-direction:column; gap:1px; align-items:center; justify-content:center; font-size:12px; }
/* Split layout (4-5 active) */
.multi-split { display:flex; gap:0; font-size:11px; }
.cell-left { flex:3; display:flex; flex-direction:column; gap:1px; padding-right:3px; border-right:0.5px solid #d0d5dd; }
.cell-right { flex:2; display:flex; flex-direction:column; gap:1px; padding-left:3px; }
.multi-split-6 { font-size:10px; }
.multi-split-6 .cell-left, .multi-split-6 .cell-right { flex:1; }
.val-line { display:flex; align-items:center; justify-content:flex-end; gap:3px; white-space:nowrap; line-height:1.2; font-weight:500; }
.vdot { display:inline-block; width:7px; height:7px; border-radius:50%; flex-shrink:0; }

/* Detail columns */
.col-det { min-width:3.5em; text-align:right; font-size:11px; font-weight:500; padding:0.25em 0.3em; }
thead .col-det { color:white; font-size:0.7em; font-weight:600; text-align:center; padding:0.3em 0.2em; }
.col-det-ppend { color:#b8860b; }
.col-det-horas { color:#999; text-align:center !important; }
.det-verde { color:#198754 !important; font-weight:500; }
.det-rojo { color:#dc3545 !important; font-weight:500; }
.dif-tpc-pos { color:#198754 !important; }
.dif-tpc-neg { color:#dc3545 !important; }

/* Columns types — auxiliares estrechas */
.col-res { min-width:65px; font-size:11px; font-weight:500; }
.col-res-obj { min-width:60px; }
.col-pct { min-width:50px; text-align:center !important; }
.col-falta-tpc { min-width:70px; text-align:right; font-size:11px; font-weight:500; }
.sem-fechas { font-size:0.8em; color:#7c8db0; }

/* Separators */
.col-sep-left { border-left:2px solid #c8d0de !important; }
thead .col-sep-left { border-left:2px solid #3d5278 !important; }

/* Footer rows — números uniformes 11px/500 */
.fila-totales td { background:#eef1f7; font-size:11px; font-weight:500; padding:0.4em 0.35em; border-top:2px solid #c8d0de; color:#1a2744; text-align:right; }
.tabla-plan tfoot td { text-align:right; font-size:11px; font-weight:500; }
.fila-obj-sem td { background:#f5f7fa; font-size:11px; color:#7c8db0; padding:0.25em 0.35em; font-weight:500; text-align:center; }
.fila-dif-sem td { font-size:11px; padding:0.25em 0.35em; font-weight:500; text-align:center; }
.fila-pct-sem td { font-size:11px; padding:0.25em 0.35em; font-weight:500; text-align:center; border-bottom:2px solid #c8d0de; }

/* Badges estado */
.badge-estado { display:inline-block; padding:0.15em 0.6em; border-radius:2em; font-weight:500; font-size:11px; letter-spacing:0.02em; }
.estado-MUY_BUENO { background:#D6EAF8; color:#1B4F72; }
.estado-BUENO { background:#D5F5E3; color:#0E6251; }
.estado-MALO { background:#FADBD8; color:#943126; }
.estado-MUY_MALO { background:#F1948A; color:#641E16; }

/* Valores */
.valor-positivo { color:#0E6251 !important; font-weight:500; }
.valor-negativo { color:#B71C1C !important; font-weight:500; }

/* Leyenda estados */
.leyenda { padding:0.6em 0.8em; display:flex; gap:1.2em; flex-wrap:wrap; font-size:0.8em; color:#7c8db0; }
.leyenda-item { display:flex; align-items:center; gap:0.4em; }
.dot { display:inline-block; width:1em; height:1em; border-radius:50%; }
.dot.estado-MUY_BUENO { background:#D6EAF8; border:1px solid #1B4F72; }
.dot.estado-BUENO { background:#D5F5E3; border:1px solid #0E6251; }
.dot.estado-MALO { background:#FADBD8; border:1px solid #943126; }
.dot.estado-MUY_MALO { background:#F1948A; border:1px solid #641E16; }

/* Column types from Producción — auxiliares estrechas */
.col-sem { min-width:40px; max-width:50px; font-size:10px; }
.col-num { min-width:50px; }
.col-dia { min-width:28px; max-width:35px; text-align:center !important; font-size:10px; color:#7c8db0; }
.dia-modificado { background:#fff3cd !important; font-weight:bold; }
.dia-ausencia { background:#FFF9E6 !important; color:#856404; font-weight:600; }
.dia-baja { background:#FEE2E2 !important; color:#991B1B; font-weight:600; }
.dia-comp { background:#EDE9FE !important; color:#5B21B6; font-weight:600; }

/* Guía de uso */
.ct-popup-guia { --f7-popup-tablet-width:700px; --f7-popup-tablet-height:85vh; }
.ct-hdr { padding:14px 16px 12px;flex-shrink:0; }
.ct-hdr-top { display:flex;justify-content:space-between;align-items:center;margin-bottom:12px; }
.ct-hdr-left { display:flex;align-items:center;gap:10px; }
.ct-hdr-ico { font-size:24px; }
.ct-hdr-title { font-size:16px;font-weight:800;color:#fff; }
.ct-hdr-sub { font-size:10px;color:rgba(255,255,255,.6);margin-top:1px; }
.ct-hdr-right { display:flex;align-items:center;gap:6px; }
.ct-hdr-badge { background:rgba(255,255,255,.2);border-radius:12px;padding:3px 10px;font-size:10px;font-weight:700;color:#fff; }
.ct-hdr-add,.ct-hdr-close { width:28px;height:28px;border-radius:6px;border:1px solid rgba(255,255,255,.25);background:rgba(255,255,255,.1);color:#fff;font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center; }
.ct-hdr-add:hover,.ct-hdr-close:hover { background:rgba(255,255,255,.25); }
.ct-search-wrap { position:relative;display:flex;align-items:center; }
.ct-search-ico { position:absolute;left:10px;font-size:13px; }
.ct-search { width:100%;padding:9px 32px;border:none;border-radius:8px;font-size:13px;background:rgba(255,255,255,.9);color:#1e293b;outline:none;box-shadow:0 2px 8px rgba(0,0,0,.1); }
.ct-search:focus { background:#fff; }
.ct-search-x { position:absolute;right:8px;background:none;border:none;color:#94a3b8;font-size:12px;cursor:pointer;padding:4px; }
.ct-body { background:linear-gradient(180deg,#f8fafc,#f0f2f5);padding:10px 14px !important; overflow-y:auto !important; -webkit-overflow-scrolling:touch; }
.ct-counter { font-size:11px;color:#64748b;font-weight:600;padding:4px 2px 8px; }
.ct-empty { text-align:center;padding:40px 20px;color:#94a3b8;font-size:12px; }
.ct-card { background:#fff;border-radius:10px;border:1px solid #e2e8f0;border-left:3px solid;margin-bottom:8px;box-shadow:0 1px 3px rgba(0,0,0,.05);overflow:hidden;cursor:grab; }
.ct-card:hover { box-shadow:0 3px 10px rgba(0,0,0,.08); }
.ct-card-hdr { display:flex;align-items:center;gap:10px;padding:10px 12px;cursor:pointer; }
.ct-card-num { width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;color:#fff;flex-shrink:0; }
.ct-card-title-area { flex:1;min-width:0; }
.ct-card-title { font-size:13px;font-weight:600;color:#1e293b;line-height:1.4; }
.ct-card-chev { font-size:10px;color:#94a3b8;flex-shrink:0; }
.ct-card-body { padding:0 12px 12px;border-top:1px solid #f1f5f9; }
.ct-card-content { font-size:12.5px;line-height:1.6;color:#334155;padding:10px 4px; }
/* #85 · La tarjeta fija de «quién aparece en este mes»: ni se arrastra ni se pliega, así que no
   hereda el cursor de arrastre de .ct-card ni el de pulsar de la cabecera. */
.ct-card-fija { cursor:default; }
.ct-card-fija .ct-card-hdr { cursor:default; }
.ct-card-fija .ct-card-content p { margin:0 0 8px; }
.ct-card-fija .ct-card-content p:last-child { margin-bottom:0; }
.ct-card-fija .ct-card-content ol { margin:0 0 8px;padding-left:18px; }
.ct-card-fija .ct-card-content li { margin-bottom:4px; }
.ct-card-actions { display:flex;gap:4px;padding-top:8px;border-top:1px solid #f1f5f9; }
.ct-card-actions button { background:none;border:1px solid #e2e8f0;border-radius:6px;padding:4px 10px;font-size:10px;cursor:pointer;color:#475569; }
.ct-card-actions button:hover { background:#f1f5f9; }
.ct-form-field { display:flex;flex-direction:column;gap:3px;margin-bottom:8px; }
.ct-form-field label { font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase; }
.ct-form-field input,.ct-form-field textarea { padding:8px 10px;border:1.5px solid #d1d9e0;border-radius:6px;font-size:12px;font-family:inherit;color:#1e293b;outline:none;resize:vertical; }
.ct-form-field input:focus,.ct-form-field textarea:focus { border-color:#1a2744; }
.ct-form-btns { display:flex;gap:6px; }
.ct-fbtn { padding:7px 16px;border:none;border-radius:6px;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit; }
.ct-fbtn-ok { background:#1a2744;color:#fff; }
.ct-fbtn-ok:hover { background:#0f1a30; }
.ct-fbtn-cancel { background:#f1f5f9;color:#475569;border:1px solid #e2e8f0; }
.ct-fbtn-cancel:hover { background:#e2e8f0; }

/* Indicadores globales */
.indicadores-grid { display:grid; grid-template-columns:repeat(11,1fr); gap:0.4em; padding:0.3em 0.6em; }
@media (max-width:900px) { .indicadores-grid { grid-template-columns:repeat(4,1fr); } }
@media (max-width:500px) { .indicadores-grid { grid-template-columns:repeat(2,1fr); } }
.ind-card { border-radius:0.5em; padding:0.35em 0.5em; text-align:center; }
.ind-label { font-size:10px; text-transform:uppercase; letter-spacing:0.03em; font-weight:600; opacity:0.7; margin-bottom:0.1em; }
.ind-val { font-size:16px; font-weight:500; }
.ind-gris { background:#f0f4fa; color:#1a2744; }
.ind-amarillo { background:#FFF9E6; color:#856404; }
.ind-rojo { background:#FEE2E2; color:#991B1B; }
.ind-azul { background:#E6F1FB; color:#0C447C; }
thead .col-dia, thead .col-sem, thead .col-num { font-size:9px; }

/* Popup persona */
/* #166 · Lleva cuatro bloques (objetivo, tabla de semanas de seis columnas, resumen del mes y, si
   la persona tiene ausencias, la tabla de ausencias detectadas), así que necesita el mismo porte
   que sus hermanos: en 480px sin altura el contenido salía apretado.
   El tope va DENTRO de la variable y no en un max-width aparte: Framework7 centra el popup con
   `margin-left:calc(-1 * var(--f7-popup-tablet-width) / 2)`, así que un max-width externo encogía
   la caja pero no el desplazamiento, y a 1920px quedaba corrida a la izquierda.
   Y el tope se pone con un @media y no con min(), que no existe en los navegadores del
   browserslist del proyecto (Chrome 49, Firefox 31): la variable aceptaría el texto igualmente,
   pero al sustituirla en el width y en el margin-left ambas declaraciones quedarían inválidas y el
   popup perdería ancho y centrado. 760px es el 72% de 1056px, así que el corte cae donde el tope
   empezaba a morder. */
.popup-persona { --f7-popup-tablet-width:72%; --f7-popup-tablet-height:84vh; }
@media (min-width:1056px) { .popup-persona { --f7-popup-tablet-width:760px; } }
/* #166 · El alto es FIJO (84vh) y no se pasa a `height:auto`. Lo que parecía un tercio de popup
   gris vacío debajo de «Quitar del mes» es el sitio de la sección «Ausencias detectadas», que solo
   se pinta cuando la persona tiene ausencias en el mes; la persona con la que se probó en preview-1
   no tenía ninguna. Decisión de dirección en la incidencia: el alto se queda como está, porque con
   el popup lleno hace falta. */
/* #166 · El popup no se desplaza nunca en horizontal: lo que no quepa lo desplaza su propio
   envoltorio de tabla (.analisis-tabla-wrap). overflow-x por separado es válido junto al
   overflow-y:auto, que se deja fijado aquí porque Framework7 trae
   `html.with-modal-popup .framework7-root > .views .page-content { overflow:hidden }` y esa regla
   gana por el selector de tipo `html` si solo se usa un nombre de clase. Sin el overflow-y, con el
   popup lleno de ausencias el final del contenido quedaría recortado sin forma de alcanzarlo. */
.popup.popup-persona ::v-deep .page-content { overflow-x:hidden; overflow-y:auto; }
/* #166 · El height:0 de antes no llegaba a aplicarse: scoped lo compila como
   `.popup-persona .navbar[data-v-…]` y la navbar la crea a mano el enhancer de src/js/app.js, sin
   ese atributo. De ahí que en producción se siguiera viendo. Con ::v-deep sí alcanza, y queda como
   red por si el enhancer vuelve a inyectarla. */
.popup-persona ::v-deep .navbar { display:none !important; }
.popup-disp .navbar { height:0 !important; min-height:0 !important; overflow:hidden !important; padding:0 !important; margin:0 !important; }
.popup-analisis .navbar { height:0 !important; min-height:0 !important; overflow:hidden !important; padding:0 !important; margin:0 !important; }
.popup-desglose .navbar { height:0 !important; min-height:0 !important; overflow:hidden !important; padding:0 !important; margin:0 !important; }

/* Popup reporte */
.popup-reporte { --f7-popup-tablet-width:500px; }

/* Disponibilidad horas */
.col-det-horas { text-align:right !important; text-decoration:none; }
.col-det-horas:hover { text-decoration:underline; }
.disp-verde { color:#198754 !important; }
.disp-naranja { color:#e6a817 !important; }
.disp-rojo { color:#dc3545 !important; }

/* Popup disponibilidad */
.popup-disp { --f7-popup-tablet-width:420px; }
.tabla-disp { width:100%; border-collapse:collapse; font-size:12px; }
.tabla-disp th { padding:6px 8px; border-bottom:2px solid #c8d0de; color:#7c8db0; font-size:11px; font-weight:600; }
.tabla-disp td { padding:5px 8px; border-bottom:1px solid #eef0f5; font-size:11px; font-weight:500; }
.fila-total-disp td { border-top:2px solid #c8d0de; background:#f5f7fa; }
.disp-fila-rojo { background:#fef2f2; }
.disp-fila-naranja { background:#fffbeb; }
.disp-fila-verde { background:#f0fdf4; }
.disp-fila-amarillo { background:rgba(230,168,23,0.15); }

/* ΔTPC clickable */
.val-dtpc { cursor:pointer; }
.val-dtpc:hover span:last-child { text-decoration:underline; }

/* Popup análisis */
.popup-analisis { --f7-popup-tablet-width:90%; --f7-popup-tablet-height:80vh; max-width:900px; }
.popup-desglose { --f7-popup-tablet-width:60%; --f7-popup-tablet-height:50vh; max-width:600px; }
.modal-manual-backdrop { position:fixed; top:0; left:0; right:0; bottom:0; background:rgba(0,0,0,0.4); z-index:13000; display:flex; align-items:center; justify-content:center; }
.modal-manual { background:#fff; border-radius:8px; width:340px; max-width:90vw; box-shadow:0 8px 32px rgba(0,0,0,0.25); }
.triangulo-m { position:absolute; top:0; left:0; width:0; height:0; border-top:16px solid #1a2744; border-right:16px solid transparent; cursor:pointer; z-index:1; opacity:0.25; transition:opacity 0.15s; }
.triangulo-m:hover { opacity:0.7; }
.triangulo-m > span { position:absolute; top:-15px; left:1px; font-size:7px; color:#fff; font-weight:700; line-height:1; pointer-events:none; }
.triangulo-override { border-top-color:#e6a817 !important; opacity:0.85; }
.triangulo-override:hover { opacity:1; }
.analisis-hdr { background:#1a2744; padding:14px 16px; display:flex; justify-content:space-between; align-items:center; }
.analisis-titulo { font-size:15px; font-weight:700; color:#fff; }
.analisis-sub { font-size:11px; color:rgba(255,255,255,.6); margin-top:2px; }
.analisis-nav { width:28px; height:28px; border-radius:6px; border:1px solid rgba(255,255,255,.25); background:rgba(255,255,255,.1); color:#fff; font-size:14px; cursor:pointer; }
.analisis-nav:hover { background:rgba(255,255,255,.25); }
.analisis-close { width:28px; height:28px; border-radius:6px; border:none; background:rgba(255,255,255,.15); color:#fff; font-size:18px; cursor:pointer; }
/* #166 · Solo el encabezado del popup de persona, todo bajo .analisis-hdr-persona para que los
   otros cinco popups que comparten .analisis-hdr queden intactos.
   «Desempeño» va agrupado con la X para que el hueco del space-between caiga entre el título y las
   acciones, y no en medio de los tres. Y el grupo de acciones no se encoge nunca: lo que cede es el
   título, que se recorta con puntos suspensivos. Así ningún elemento del encabezado puede acabar
   fuera del ancho interior del popup ni montado sobre la X. */
.analisis-hdr-persona { flex-wrap:nowrap; padding-right:20px; }
.analisis-hdr-persona .analisis-hdr-txt { min-width:0; overflow:hidden; }
.analisis-hdr-persona .analisis-titulo,
.analisis-hdr-persona .analisis-sub { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.analisis-hdr-persona .analisis-hdr-acciones { display:flex; align-items:center; flex:0 0 auto; }
.analisis-hdr-persona .analisis-close { flex:0 0 auto; }
.analisis-kpis { display:flex; gap:8px; margin:12px 0 8px; }
.analisis-kpi { flex:1; background:#f8fafe; border:1px solid #e8edf3; border-radius:8px; padding:8px 10px; }
.analisis-kpi-label { font-size:10px; color:#7c8db0; text-transform:uppercase; font-weight:600; }
.analisis-kpi-val { font-size:16px; font-weight:700; margin-top:2px; }
.analisis-causas { display:flex; gap:5px; flex-wrap:wrap; margin-bottom:10px; }
.causa-badge { font-size:10px; padding:3px 8px; border-radius:4px; font-weight:600; white-space:nowrap; }
.causa-badge-sm { font-size:9px; padding:2px 6px; border-radius:4px; font-weight:600; color:#fff; white-space:nowrap; display:inline-block; }
.analisis-tabla-wrap { overflow-x:auto; }
.analisis-tabla { width:100%; border-collapse:collapse; font-size:11px; }
.analisis-tabla th { padding:5px 6px; background:#f5f7fa; border-bottom:2px solid #c8d0de; color:#7c8db0; font-size:10px; font-weight:600; text-align:right; white-space:nowrap; }
.analisis-tabla td { padding:4px 6px; border-bottom:1px solid #eef0f5; font-size:11px; text-align:right; white-space:nowrap; }
.analisis-tabla .fila-alt { background:#fafbfd; }
.btn-ver { padding:2px 8px; border:1px solid #0d6efd; border-radius:4px; background:white; color:#0d6efd; font-size:10px; cursor:pointer; white-space:nowrap; }
.btn-ver:hover { background:#0d6efd; color:white; }
.fila-ausencia { background:#FFF9E6; }
</style>
<script>
import { unwrapDto } from '../../js/db/responseUtils.js';
import avisoNoGuardado from '../../js/utils/avisoNoGuardado.js';
import Oeb from '../../js/db/Oeb.js';
import Desempeno from '../../js/db/Desempeno.js';
import InfoIcon from '../../components/InfoIcon.vue';
import SelloDatos from '../../components/SelloDatos.vue';
import carrilPeticiones from '../../js/utils/carrilPeticiones.js';
import preloaderDeLaSesion from '../../js/utils/preloaderDeLaSesion.js';
import cerrojoDeLaSesion from '../../js/utils/cerrojoDeLaSesion.js';
import { atarALaSesion, comoPromesaComun } from '../../js/utils/sesionDePeticiones.js';

// #46 · Plazo propio de la pantalla para la ficha del mes. Sin él, una petición que no vuelve
// nunca (corte de red, pasarela que se queda colgada) deja el preloader girando para siempre y
// nadie sabe qué ha pasado. Holgado a propósito: la carga mala que se está midiendo en #45 ronda
// los 65 s, así que 120 s no corta una carga lenta pero viva.
var ESPERA_FICHA_MS = 120000;

// #45 · Y el mismo plazo para la petición suelta de INF.PEND., que sale del mismo servidor y pasa
// por la misma pasarela: si se queda colgada, sin plazo el aviso «Calculando INF.PEND…» no se
// apaga nunca y la guarda de `asegurarInfPend()` impide reintentarlo.
var ESPERA_INF_PEND_MS = ESPERA_FICHA_MS;

// #769 · El plazo de la ficha como un fallo más de la petición: así lo decide el carril igual que
// un 500 —limpia y avisa si es la vigente, y si ya no lo es no toca nada— y no hace falta un reloj
// que apague el «cargando» por su cuenta. La petición no se puede abortar; solo se deja de esperar.
function conPlazo(promesa, ms) {
  let reloj;
  const vencido = new Promise(function(_, rechazar) {
    reloj = setTimeout(function() { rechazar({ plazoVencido: true }); }, ms);
  });
  return Promise.race([promesa, vencido]).then(
    function(res) { clearTimeout(reloj); return res; },
    function(err) { clearTimeout(reloj); throw err; });
}

export default {
  components: { InfoIcon, SelloDatos },
  data: function() {
    return {
      anio: new Date().getFullYear(),
      mes: new Date().getMonth() + 1,
      ficha: null,
      desglose: {},
      tpcOriginal: {},
      overrides: {},
      ausencias: {},
      numIcData: {},
      horasDiariasData: {},
      compensacionData: {},
      guardandoIc: false,
      popupInfo: false,
      fullscreenInfo: false,
      instrucciones: [],
      busquedaInst: '',
      expandedInst: null,
      draggingInst: null,
      cargando: false,
      // #46 · Por qué no hay tabla: 'TIEMPO' (se pasó el plazo o la pasarela cortó la petición),
      // 'ERROR' (la llamada falló o la respuesta llegó sin ficha utilizable) o null (todo normal,
      // incluido el mes que todavía no tiene ficha creada, que tiene su propio bloque).
      errorCarga: null,
      zoom: 100,
      toggles: { oeb: true, oebR: true, oebP: false, oebPPend: false, tpc: true, difTpc: false, infPend: false },
      // codigoPersonal -> importe pendiente por semana (null en las semanas futuras).
      infPend: {},
      // INF.PEND. ya no viaja siempre con la ficha (#45): estas dos dicen si la vista que se está
      // mirando ya la tiene y si está de camino.
      infPendCargado: false,
      cargandoInfPend: false,
      // tipoSemana[] (PASADA/ACTUAL/FUTURA) + lineasSinFechaInforme, tal y como los manda la API.
      infPendMeta: {},
      // El texto de la (i) de INF.PEND. va aquí y no en el catálogo de conceptos porque el catálogo
      // vive en base de datos y este concepto nace con la pantalla: pasándolo como prop, la ayuda
      // sale desde el primer despliegue sin depender de que nadie dé de alta nada.
      conceptoInfPend: {
        clave: 'OEB_INF_PEND',
        titulo: 'INF.PEND. — informe ejecutado y sin emitir',
        criticidad: 'CRITICO',
        texto: 'Lo que cada persona lleva encima en informes ya ejecutados en campo pero todavía sin emitir. '
          + 'Sobre Reparto de Ejecución NEW: USU.EJEC.REPARTO = la persona, TPC Lín = SÍ, TPI Lín = NO, '
          + 'y se suma el IMPORTE REPARTO, es decir el importe imputado a esa persona.',
        formula: 'SUMA(IMPORTE REPARTO) con TPC Lín = SÍ y TPI Lín = NO,\n'
          + 'por USU.EJEC.REPARTO, sólo líneas de pedidos activos en estado\n'
          + 'CREADO / PLANIFICADO / EN_EJECUCION / EJECUTADO / FACTURADO / CERRADO\n'
          + '(fuera CANCELADO, igual que el job nocturno de informes pendientes).',
        contextoUso: 'Puede NO cuadrar con el panel de informes pendientes por inspector, y es correcto: '
          + 'aquel mide el backlog de la empresa y suma el importe COMPLETO de la línea; esta vista mide '
          + 'personas y suma sólo la parte imputada a cada una, así que en las líneas con reparto '
          + 'compartido sale menos. — Semanas cerradas: la foto del domingo a las 23:59 (hora peninsular), '
          + 'que ya no cambia aunque después se emitan esos informes. Sí cambia, en cambio, si alguien '
          + 'corrige el importe de la línea o el porcentaje de reparto: no se guarda el importe que tenía '
          + 'cada domingo, así que la semana pasada se vuelve a calcular con el importe corregido. '
          + 'Semana en curso: el pendiente de hoy, vivo. Semanas futuras: en blanco.',
        dependencias: ['OEB_TPC_SI']
      },
      mostrarDetalle: false,
      popupReporte: false,
      reporteTipo: 'MENSUAL',
      reporteEmails: '',
      reporteCc: '',
      reporteAsunto: '',
      enviandoReporte: false,
      popupDisp: false,
      dispPersona: null,
      dispSemIdx: 0,
      popupPersona: false,
      guardandoPersonas: false,
      personaSel: null,
      // C5: ficha de desempeño (drill-down)
      popupDesempeno: false,
      desempeno: null,
      desempenoLoading: false,
      objetivoEditable: 0,
      popupAnalisis: false,
      analisisMostrarOk: false,
      popupDesglose: false,
      desgloseCod: '',
      desgloseSemNum: 0,
      desgloseSemIdx: 0,
      desgloseDias: [],
      popupManual: false,
      manualMotivo: '',
      manualError: '',
      manualCod: '',
      manualSemIdx: 0,
      manualSemNum: 0,
      manualValor: '',
      manualInfo: null,
      analisisData: null,
      analisisPersonaCod: '',
      analisisSemIdx: 0,
      analisisCache: {}
    };
  },
  computed: {
    nombresMeses: function() {
      return ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
    },
    aniosDisponibles: function() {
      var arr = [];
      for (var a = 2020; a <= new Date().getFullYear() + 1; a++) arr.push(a);
      return arr;
    },
    instFiltradas: function() {
      var q = (this.busquedaInst || '').toLowerCase();
      if (!q) return this.instrucciones;
      return this.instrucciones.filter(function(i) { return (i.titulo || '').toLowerCase().indexOf(q) > -1 || (i.texto || '').toLowerCase().indexOf(q) > -1; });
    },
    indTotalDH: function() {
      if (!this.ficha || !this.ficha.personas) return 0;
      var self = this;
      return this.ficha.personas.reduce(function(s, p) { return s + self.calcularDiasHabilesPersona(p); }, 0);
    },
    indTotalVacaciones: function() {
      if (!this.ficha || !this.ficha.personas) return 0;
      return this.ficha.personas.reduce(function(s, p) { return s + (p.diasVacaciones || 0); }, 0);
    },
    indTotalBajas: function() {
      if (!this.ficha || !this.ficha.personas || !this.desglose) return 0;
      var self = this;
      return this.ficha.personas.reduce(function(s, p) { return s + self.getDesgloseBL(p.codigoPersonal); }, 0);
    },
    indTotalFiestas: function() {
      if (!this.ausencias) return 0;
      var total = 0;
      for (var cod in this.ausencias) {
        var aus = this.ausencias[cod];
        for (var i = 0; i < aus.length; i++) {
          if (aus[i].motivo && aus[i].motivo.toUpperCase().indexOf('FIESTA') >= 0) total++;
        }
      }
      return total;
    },
    indTotalDR: function() {
      if (!this.ficha || !this.ficha.personas) return 0;
      return this.ficha.personas.reduce(function(s, p) { return s + (p.diasReales || 0); }, 0);
    },
    indProductividadPersona: function() {
      if (!this.ficha || !this.ficha.personas || !this.ficha.personas.length) return 0;
      var self = this;
      var tpcTotal = 0;
      self.ficha.personas.forEach(function(p) { tpcTotal += self.sumaTpcPersona(p.codigoPersonal); });
      return tpcTotal / self.ficha.personas.length;
    },
    indTotalNumIc: function() {
      if (!this.ficha || !this.ficha.personas) return 0;
      var self = this;
      return this.ficha.personas.reduce(function(s, p) { return s + self.getNumIc(p.codigoPersonal); }, 0);
    },
    indTotalTpc: function() {
      if (!this.ficha || !this.ficha.personas) return 0;
      var self = this; var t = 0;
      self.ficha.personas.forEach(function(p) { t += self.sumaTpcPersona(p.codigoPersonal); });
      return t;
    },
    indAvgHD: function() {
      if (!this.ficha || !this.ficha.personas || !this.ficha.personas.length) return 8;
      var self = this;
      return this.ficha.personas.reduce(function(s, p) { return s + self.getHorasDiarias(p.codigoPersonal); }, 0) / self.ficha.personas.length;
    },
    indTotalHorasMes: function() {
      if (!this.ficha || !this.ficha.personas) return 0;
      var self = this; var total = 0;
      self.ficha.personas.forEach(function(p) { total += self.getHorasDiarias(p.codigoPersonal) * (p.diasReales || 0); });
      return Math.round(total);
    },
    indPrecioMedioHora: function() {
      if (this.indTotalHorasMes <= 0) return '0,0';
      return (this.indTotalTpc / this.indTotalHorasMes).toFixed(1).replace('.', ',');
    },
    indTotalCompensacion: function() {
      if (!this.compensacionData) return 0;
      var total = 0;
      for (var cod in this.compensacionData) total += this.compensacionData[cod];
      return Math.round(total);
    },
    indProductividadIc: function() {
      if (!this.ficha || !this.ficha.personas || this.indTotalNumIc <= 0) return 0;
      var self = this;
      var tpcTotal = 0;
      self.ficha.personas.forEach(function(p) { tpcTotal += self.sumaTpcPersona(p.codigoPersonal); });
      return tpcTotal / self.indTotalNumIc;
    },
    nombreMes: function() {
      var meses = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      return meses[this.mes] || '';
    },
    numToggleActivos: function() {
      var c = 0;
      if (this.toggles.oeb) c++;
      if (this.toggles.oebR) c++;
      if (this.toggles.oebP) c++;
      if (this.toggles.oebPPend) c++;
      if (this.toggles.tpc) c++;
      if (this.toggles.difTpc) c++;
      if (this.toggles.infPend) c++;
      return c || 1;
    },
    infPendSinFecha: function() {
      return (this.infPendMeta && this.infPendMeta.lineasSinFechaInforme) || 0;
    },
    personasOrdenadas: function() {
      if (!this.ficha || !this.ficha.personas) return [];
      return [].concat(this.ficha.personas).sort(function(a, b) {
        return a.codigoPersonal.localeCompare(b.codigoPersonal);
      });
    },
    semTotales: function() {
      if (!this.ficha || !this.ficha.personas || !this.ficha.calendario) return [];
      var self = this;
      var numSem = self.ficha.calendario.semanas.length;
      var totales = [];
      for (var j = 0; j < numSem; j++) {
        var t = { oeb: 0, oebR: 0, oebP: 0, oebPPend: 0, tpc: 0, infPend: null };
        var jj = j;
        self.ficha.personas.forEach(function(p) {
          t.oeb += (p.resultadosSemana && p.resultadosSemana[jj]) || 0;
          t.oebR += self.getDesgloseVal(p.codigoPersonal, 'oebR', jj);
          t.oebP += self.getDesgloseVal(p.codigoPersonal, 'oebP', jj);
          t.oebPPend += self.getDesgloseVal(p.codigoPersonal, 'oebPPend', jj);
          t.tpc += self.getTpcVal(p.codigoPersonal, jj);
          // El TOTAL de INF.PEND. arranca en null y solo se convierte en número cuando alguna
          // persona tiene dato: en una semana futura todas valen null y la fila TOTAL queda
          // también en blanco, no en 0.
          var ip = self.getInfPendVal(p.codigoPersonal, jj);
          if (ip != null) t.infPend = (t.infPend || 0) + ip;
        });
        totales.push(t);
      }
      return totales;
    },
    nombreMesSiguiente: function() {
      var m = this.mes === 12 ? 1 : this.mes + 1;
      var a = this.mes === 12 ? this.anio + 1 : this.anio;
      var meses = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
      return meses[m] + ' ' + a;
    },
    nombreMesCorto: function() {
      var m = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
      return m[this.mes] || '';
    },
    dispSemNum: function() {
      if (!this.ficha || !this.ficha.calendario || !this.ficha.calendario.semanas[this.dispSemIdx]) return 0;
      return this.ficha.calendario.semanas[this.dispSemIdx].numero;
    },
    dispSemRango: function() {
      if (!this.ficha || !this.ficha.calendario) return '';
      var sem = this.ficha.calendario.semanas[this.dispSemIdx];
      return sem ? this.extraerDias(sem) : '';
    },
    dispFilas: function() {
      if (!this.dispPersona || !this.desglose) return [];
      var cod = this.dispPersona.codigoPersonal;
      var des = this.desglose[cod];
      if (!des || !des.detalleDiario || !des.detalleDiario[this.dispSemIdx]) return [];
      var dias = des.detalleDiario[this.dispSemIdx];
      var self = this;
      var nombresDia = ['Dom','Lun','Mar','Mi\u00e9','Jue','Vie','S\u00e1b'];
      var ausPersona = self.ausencias[cod] || [];
      return dias.map(function(d) {
        var fecha = new Date(self.anio, self.mes - 1, d.dia);
        var dayOfWeek = fecha.getDay();
        var esLaboral = dayOfWeek > 0 && dayOfWeek < 6;
        var ausItem = ausPersona.find(function(a) { return a.dia === d.dia; });
        var motivo = ausItem ? ausItem.motivo : null;
        var disp = esLaboral && !motivo ? (480 - d.minutos) : 0;
        var cls = '';
        if (motivo) cls = 'disp-fila-amarillo';
        else if (disp <= 0) cls = 'disp-fila-rojo';
        else if (disp < 120) cls = 'disp-fila-naranja';
        else cls = 'disp-fila-verde';
        return { dia: d.dia, minutos: d.minutos, disponible: disp, nombreDia: nombresDia[fecha.getDay()], dispClase: cls, motivo: motivo };
      });
    },
    dispTotalPlan: function() {
      return this.dispFilas.reduce(function(s, d) { return s + d.minutos; }, 0);
    },
    dispTotalDisp: function() {
      return this.dispFilas.reduce(function(s, d) { return s + d.disponible; }, 0);
    },
    analisisSemActual: function() {
      if (!this.analisisData || !this.analisisData.personas || !this.analisisData.personas.length) return null;
      var semanas = this.analisisData.personas[0].semanas || [];
      return semanas[this.analisisSemIdx] || null;
    },
    analisisCausasResumen: function() {
      var sem = this.analisisSemActual;
      if (!sem || !sem.items) return [];
      var map = {};
      sem.items.forEach(function(it) {
        if (it.causa === 'OK') return;
        if (!map[it.causa]) map[it.causa] = { causa: it.causa, importe: 0 };
        map[it.causa].importe += Math.abs(it.diferencia || 0);
      });
      return Object.values(map).sort(function(a, b) { return b.importe - a.importe; });
    },
    analisisItemsFiltrados: function() {
      var sem = this.analisisSemActual;
      if (!sem || !sem.items) return [];
      if (this.analisisMostrarOk) return sem.items;
      return sem.items.filter(function(it) { return it.causa !== 'OK'; });
    },
    analisisItemsTotalOeb: function() {
      var sem = this.analisisSemActual;
      if (!sem || !sem.items) return 0;
      return sem.items.reduce(function(s, it) { return s + (it.importeOEB || 0); }, 0);
    },
    analisisItemsTotalTpc: function() {
      var sem = this.analisisSemActual;
      if (!sem || !sem.items) return 0;
      return sem.items.reduce(function(s, it) { return s + (it.importeTPC || 0); }, 0);
    },
    analisisItemsTotalDif: function() {
      var sem = this.analisisSemActual;
      if (!sem || !sem.items) return 0;
      return sem.items.reduce(function(s, it) { return s + (it.diferencia || 0); }, 0);
    },
    desgloseDisponDiasHab: function() {
      if (!this.ficha || !this.ficha.personas) return 0;
      var p = this.ficha.personas.find(function(pp) { return pp.codigoPersonal === this.desgloseCod; }.bind(this));
      if (!p || !p.semanas || !p.semanas[this.desgloseSemIdx]) return 0;
      return p.semanas[this.desgloseSemIdx].diasHabiles || 0;
    },
    desgloseMinutosOcupados: function() {
      if (!this.desgloseDias) return 0;
      return this.desgloseDias.reduce(function(s, d) { return s + (d.minutos || 0); }, 0);
    },
    // OEB y TPC leídos de la TABLA PRINCIPAL (fuente de verdad: registros DIA del cache
    // listadocalendarios). El endpoint de análisis de diferencias tiene filtros distintos
    // al cache DIA (no filtra esVersion, rol.tipo!="I", etc.) y puede dar importes
    // inconsistentes. Por eso los KPIs del popup leen de la tabla, no del endpoint.
    analisisOebDesdeTabla: function() {
      var sem = this.analisisSemActual;
      if (!sem || !this.ficha || !this.ficha.personas || !this.ficha.calendario) return 0;
      var semNum = sem.semana;
      var semIdx = -1;
      var semanas = this.ficha.calendario.semanas || [];
      for (var i = 0; i < semanas.length; i++) {
        if (semanas[i].numero === semNum) { semIdx = i; break; }
      }
      if (semIdx < 0) return 0;
      var cod = this.analisisPersonaCod;
      var persona = null;
      for (var j = 0; j < this.ficha.personas.length; j++) {
        if (this.ficha.personas[j].codigoPersonal === cod) { persona = this.ficha.personas[j]; break; }
      }
      if (!persona || !persona.resultadosSemana) return 0;
      return persona.resultadosSemana[semIdx] || 0;
    },
    analisisTpcDesdeTabla: function() {
      var sem = this.analisisSemActual;
      if (!sem || !this.ficha || !this.ficha.calendario) return 0;
      var semNum = sem.semana;
      var semIdx = -1;
      var semanas = this.ficha.calendario.semanas || [];
      for (var i = 0; i < semanas.length; i++) {
        if (semanas[i].numero === semNum) { semIdx = i; break; }
      }
      if (semIdx < 0) return 0;
      return this.getTpcVal(this.analisisPersonaCod, semIdx);
    },
    totalFaltaTpcVal: function() {
      if (!this.ficha || !this.ficha.personas) return 0;
      var self = this;
      var oebTotal = self.ficha.totales ? self.ficha.totales.progresionTotal || 0 : 0;
      var tpcTotal = 0;
      self.ficha.personas.forEach(function(p) { tpcTotal += self.sumaTpcPersona(p.codigoPersonal); });
      return tpcTotal - oebTotal;
    }
  },
  // Si se sale de la pantalla dentro de la ventana de agrupado, el guardado pendiente se dispara
  // ya: si no, apagar una vista y salir corriendo la dejaría encendida al volver.
  // #769 · La ficha del mes, las acciones que la recargan y su «cargando» van por el carril común
  // de la #472: tira el mes de antes en cuanto se pide otro —antes se quedaba debajo del selector
  // nuevo hasta que contestaba el servidor— y solo apaga el «cargando» la respuesta vigente.
  // Recargar el MISMO mes («Actualizar cifras», guardar, añadir a alguien) no vacía: ahí las cifras
  // son las de hace un segundo y vaciarlas sería un parpadeo.
  created: function() {
    const self = this;
    self.carrilFicha = carrilPeticiones({
      deAhora: function() { return [self.anio, self.mes]; },
      vaciar: function() { self.vaciarFicha(); },
      cargando: function(si) { self.cargando = si; }
    });
  },
  beforeDestroy: function() {
    this.guardarTogglesAhora();
    clearTimeout(this._plazoInfPend);
    // #46 · Lo que conteste ya no tiene a quién avisar si nadie está mirando la pantalla.
    this.carrilFicha.invalidar();
  },
  methods: {
    zoomIn: function() { this.zoom = Math.min(150, this.zoom + 5); },
    zoomOut: function() { this.zoom = Math.max(40, this.zoom - 5); },
    // Las vistas de la botonera se recuerdan solas. Antes solo se guardaban de rebote al pulsar
    // «Guardar I/C», así que encender una vista y salir de la pantalla la perdía, y quien no tenga
    // I/C que guardar no tenía forma de que se quedara.
    alternarToggle: function(clave) {
      this.$set(this.toggles, clave, !this.toggles[clave]);
      var self = this;
      // INF.PEND. no viene con la ficha si el botón estaba apagado (#45): al encenderlo se pide,
      // y solo esa vista, no la pantalla entera.
      if (clave === 'infPend') self.asegurarInfPend();
      // La botonera se ve en cuanto llega la ficha, antes de que conteste la lectura de
      // preferencias. Si el usuario pulsa mientras ese GET viaja, lo que conteste ya es viejo para
      // ESA vista: manda lo que acaba de pulsar, y aplicarlo encima apagaría el botón solo y
      // guardaría el valor antiguo al vencer el temporizador. Se anota vista a vista, no con un
      // «tocó algo»: descartar la respuesta entera dejaría las demás vistas en sus valores por
      // defecto y el guardado agrupado borraría las que el usuario sí tenía guardadas.
      if (!self._togglesTocados) self._togglesTocados = {};
      self._togglesTocados[clave] = true;
      // Se agrupan los cambios seguidos: encender tres vistas de un tirón es una sola escritura.
      if (self._guardadoToggles) clearTimeout(self._guardadoToggles);
      self._guardadoToggles = setTimeout(function() { self.guardarTogglesAhora(); }, 700);
    },
    // Lee las vistas guardadas y aplica las que el usuario no haya pulsado ya en esta visita.
    // Propaga el error a propósito: el guardado tiene que poder distinguir «no hay nada guardado»
    // de «no se ha podido leer».
    leerPreferenciasToggles: function() {
      var self = this;
      return Oeb.getPreferencias('control-cifras-oeb-planificacion').then(function(resPref) {
        var prefs = unwrapDto(resPref);
        var tocados = self._togglesTocados || {};
        if (prefs && prefs.toggles) {
          Object.keys(self.toggles).forEach(function(k) {
            // Las vistas que el usuario ya ha pulsado se quedan como las dejó; las demás sí
            // toman lo guardado, para no reenviarlas después con el valor por defecto.
            if (tocados[k]) return;
            if (prefs.toggles[k] != null) self.$set(self.toggles, k, prefs.toggles[k]);
          });
        }
        self._prefsLeidas = true;
      });
    },
    // Antes de escribir las vistas hay que haberlas leído. Si el GET tarda más que el temporizador,
    // `this.toggles` todavía son los valores por defecto salvo el que se acaba de pulsar, y el PUT
    // borraría las vistas guardadas que el usuario no ha tocado. Y que la lectura haya terminado no
    // basta: si falló (un 500 pasajero, un corte de red) pasa exactamente lo mismo, así que se
    // reintenta una vez y, si vuelve a fallar, esto rechaza y no se escribe nada.
    //
    // #663 · `_prefsCargadas` sobrevive al cambio de sesión (la ruta es `keepAlive`): colgar de ella
    // sin más heredaba su cancelación y la cuenta siguiente no guardaba nunca. Se adopta como
    // promesa común y su desenlace no importa, igual que antes: manda `_prefsLeidas`.
    esperarPreferenciasLeidas: function() {
      var self = this;
      return comoPromesaComun(self._prefsCargadas || Promise.resolve()).catch(function() {}).then(function() {
        if (self._prefsLeidas) return;
        return self.leerPreferenciasToggles();
      });
    },
    // Los guardados de la botonera salen en cola, de uno en uno. Si al vencer un temporizador
    // todavía viaja el PUT anterior, dos escrituras sueltas pueden completarse en orden distinto
    // al que salieron y la vieja pisaría en el servidor la selección más reciente. Encadenadas, la
    // última que sale es la última que escribe, y además cada una envía `toggles` tal como esté al
    // salir —no como estaba al programarse—, así que la que espera acaba mandando ya lo último.
    encolarGuardadoToggles: function() {
      var self = this;
      // Un guardado fallido no puede romper la cola: el siguiente tiene que salir igual. Tampoco
      // uno cancelado por cambiar de sesión, de ahí `comoPromesaComun`. Y lo que se encola se ata a
      // la sesión que lo encoló (#663): si cambia mientras espera turno, no sale el PUT con las
      // vistas de la cuenta anterior ni su toast cae en la siguiente.
      const cola = comoPromesaComun(self._colaToggles || Promise.resolve()).catch(function() {});
      const guardado = atarALaSesion(cola)
        .then(function() { return self.esperarPreferenciasLeidas(); })
        .then(function() {
          return Oeb.guardarPreferencias('control-cifras-oeb-planificacion', { toggles: self.toggles });
        });
      self._colaToggles = guardado;
      return guardado;
    },
    guardarTogglesAhora: function() {
      if (!this._guardadoToggles) return;
      clearTimeout(this._guardadoToggles);
      this._guardadoToggles = null;
      // Sin toast ni aviso de error: es una preferencia de visualización, y si falla lo único que
      // pasa es que la próxima vez la pantalla abre con los valores de antes.
      this.encolarGuardadoToggles().catch(function() {});
    },
    onPageInit: function() {
      var guardadoAnio = localStorage.getItem('oeb_plan_ultimo_anio');
      var guardadoMes = localStorage.getItem('oeb_plan_ultimo_mes');
      if (guardadoAnio) this.anio = parseInt(guardadoAnio);
      if (guardadoMes) this.mes = parseInt(guardadoMes);
      const self = this;
      // #769 · Las vistas guardadas se leen UNA vez por visita y a la vez que la ficha. Antes se
      // pedían después de cada ficha —también al cambiar de mes, aunque son del usuario y no del
      // mes— y, con INF.PEND. encendido, la pantalla esperaba tres viajes seguidos (ficha →
      // preferencias → INF.PEND.) en vez de dos.
      // La promesa se guarda porque el guardado agrupado espera a que esta lectura entre. El fallo
      // se traga aquí para no dejar un rechazo suelto: quien va a escribir mira `_prefsLeidas`, que
      // solo se pone a true si la lectura llegó a entrar, y reintenta antes de escribir.
      self._prefsLeidas = false;
      // Si el usuario tenía INF.PEND. encendido, la vista se pide al aplicar las preferencias. Si
      // la ficha todavía no ha llegado, `asegurarInfPend` no hace nada y la pide la ficha al entrar.
      self._prefsCargadas = self.leerPreferenciasToggles()
        .then(function() { self.asegurarInfPend(); })
        .catch(function() {});
      self.cargarFicha();
    },
    cargarFicha: function() {
      localStorage.setItem('oeb_plan_ultimo_anio', this.anio);
      localStorage.setItem('oeb_plan_ultimo_mes', this.mes);
      var self = this;
      // #85 · La lista de «+ Persona» que estuviera abierta es del mes de antes.
      self.cerrarListaPersonas();
      // INF.PEND. solo si el botón está encendido (#45). Al abrir la pantalla todavía no han
      // llegado las preferencias del usuario, así que aquí sale apagado y, si venía encendido, lo
      // pide `asegurarInfPend()` en cuanto se apliquen. Al cambiar de mes el botón ya está puesto y
      // viene en la misma respuesta, sin segunda vuelta.
      var pedirInfPend = self.toggles.infPend;
      self.infPendCargado = false;
      // Recargar la ficha deja vieja la petición suelta de INF.PEND. que estuviera en vuelo, y no
      // solo al cambiar de mes: «Actualizar cifras», añadir o quitar una persona y guardar un
      // cambio recargan el MISMO mes, y la respuesta en vuelo es de antes de esa modificación.
      // Cambiando de generación se descarta aunque año y mes coincidan.
      self._infPendGen = (self._infPendGen || 0) + 1;
      self.cargandoInfPend = false;
      // Y su plazo se va con ella: la generación ya lo deja sin efecto, pero un temporizador vivo
      // que no tiene nada que avisar sobra.
      clearTimeout(self._plazoInfPend);
      // #46 · Solo la carga vigente escribe en la pantalla. Cambiar de mes y el botón Reintentar
      // pueden dejar una petición anterior en vuelo, y la que llega tarde no puede pisar lo que
      // ya se está viendo ni resucitar un aviso que ya no toca. Eso, y tirar el mes anterior al
      // pedir otro, lo decide el carril (#769).
      self.errorCarga = null;
      self.carrilFicha.lanzar(conPlazo(Oeb.getFichaPlanificacion(self.anio, self.mes, pedirInfPend), ESPERA_FICHA_MS), {
        exito: function(res) {
          var datos = unwrapDto(res);
          if (datos && datos.ficha) {
            self.ficha = datos.ficha;
            self.desglose = datos.desglose || {};
            self.tpcOriginal = datos.tpcOriginal || {};
            self.infPend = datos.infPend || {};
            self.infPendMeta = datos.infPendMeta || {};
            self.infPendCargado = pedirInfPend;
            self.overrides = datos.overrides || {};
            self.ausencias = datos.ausencias || {};
            self.numIcData = datos.numIc || {};
            self.horasDiariasData = datos.horasDiarias || {};
            self.compensacionData = datos.compensacion || {};
            if (self.personaSel && self.ficha.personas) {
              var cod = self.personaSel.codigoPersonal;
              var act = self.ficha.personas.find(function(p) { return p.codigoPersonal === cod; });
              if (act) { self.personaSel = act; self.objetivoEditable = act.objetivoDiarioActual || 0; }
            }
          } else {
            self.vaciarFicha();
            // #46 · Una respuesta sin ficha son dos cosas distintas y no se pueden contar igual:
            // «este mes no tiene ficha todavía» es normal y tiene su bloque con el botón de
            // crearla; cualquier otro motivo es un fallo de carga y hay que avisar. La API lo dice
            // en `sinFicha` (NO_CREADA / INCOMPLETA / DESCONOCIDO). Si no viene —API sin el cambio
            // de esta misma tarea desplegado— se mantiene lo de antes y no se inventa un error.
            self.errorCarga = (datos && datos.sinFicha && datos.sinFicha !== 'NO_CREADA') ? 'ERROR' : null;
          }
          // Si las preferencias encendieron INF.PEND. antes de que llegara la ficha, o esta vino
          // sin ella, se pide ahora. Sin trabajo en el resto de casos.
          self.asegurarInfPend();
        },
        limpiar: function() { self.vaciarFicha(); },
        // El aviso sustituye al toast de antes: duraba 4 s y dejaba detrás una pantalla que
        // parecía decir «no hay nada». Se distingue la petición cortada por plazo o pasarela
        // —que no significa que no haya datos, y por eso el mensaje no habla de error— del
        // resto de fallos.
        fallo: function(err) {
          self.errorCarga = (err && err.plazoVencido) || Oeb.esResultadoIncierto(err) ? 'TIEMPO' : 'ERROR';
        }
      });
    },
    // Trae INF.PEND. del mes que se está mirando si hace falta: el botón encendido y la vista sin
    // cargar todavía. Idempotente a propósito, que la llaman dos sitios —pulsar el botón y aplicar
    // las preferencias guardadas al abrir— y en el caso normal solo una de las dos tiene trabajo.
    asegurarInfPend: function() {
      var self = this;
      if (!self.toggles.infPend || self.infPendCargado || self.cargandoInfPend) return;
      if (!self.ficha) return;
      // Solo se pinta lo que conteste la petición vigente. Comparar año y mes no basta: la ficha se
      // recarga también sin cambiar de mes y entonces la respuesta en vuelo es una foto anterior a
      // esa recarga, que se colaría por coincidir el mes. La generación distingue las dos.
      var gen = self._infPendGen = (self._infPendGen || 0) + 1;
      // #663 · El cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla el `then`
      // que lo soltaba y la instancia (`keepAlive`) lo conservaría puesto en la cuenta siguiente.
      cerrojoDeLaSesion(self, 'cargandoInfPend');
      // Esta petición tampoco vuelve siempre, igual que la de la ficha. Y aquí quedarse esperando
      // para siempre no es solo un reloj girando: `cargandoInfPend` en true deja puesto el
      // «Calculando INF.PEND…» y hace que la guarda de arriba se salte cualquier intento
      // posterior, incluso apagando y volviendo a encender el botón, así que la columna no vuelve
      // sin recargar la pantalla entera. Al vencer el plazo se cambia de generación: lo que
      // conteste tarde ya no pinta nada y volver a pulsar el botón sí pide de nuevo.
      clearTimeout(self._plazoInfPend);
      self._plazoInfPend = setTimeout(function() {
        if (gen !== self._infPendGen) return;
        self._infPendGen = gen + 1;
        self.cargandoInfPend = false;
        self.fallarInfPend(true);
      }, ESPERA_INF_PEND_MS);
      Oeb.getInfPendPlanificacion(self.anio, self.mes)
        .then(function(res) {
          if (gen !== self._infPendGen) return;
          var datos = unwrapDto(res) || {};
          self.infPend = datos.infPend || {};
          self.infPendMeta = datos.infPendMeta || {};
          self.infPendCargado = true;
        })
        .catch(function() {
          if (gen !== self._infPendGen) return;
          self.fallarInfPend(false);
        })
        // Quien ya no es la petición vigente no apaga el aviso de la que sí lo es, ni desarma un
        // plazo que ya no es el suyo.
        .then(function() {
          if (gen !== self._infPendGen) return;
          clearTimeout(self._plazoInfPend);
          self.cargandoInfPend = false;
        });
    },
    // El final malo de INF.PEND. es el mismo se haya caído la petición o se haya vencido el plazo:
    // la columna se queda sin cifras —media foto sería peor que ninguna— y se dice cómo reintentar,
    // que aquí es volver a pulsar el botón. El motivo sí cambia el texto: que tarde de más no
    // significa que haya fallado nada.
    fallarInfPend: function(porPlazo) {
      this.infPend = {};
      this.infPendMeta = {};
      var texto = porPlazo
        ? 'INF.PEND. esta tardando demasiado y se ha dejado de esperar. Vuelve a pulsar el boton para reintentarlo.'
        : 'No se ha podido cargar INF.PEND. Vuelve a pulsar el boton o avisa a soporte.';
      this.$f7.toast.create({ text: texto, position: 'top', closeTimeout: 4000 }).open();
    },
    // #46 · Deja la pantalla sin nada que pintar. Único sitio donde se borra, para que los tres
    // finales malos de la carga (sin ficha, fallo y plazo vencido) no se separen con el tiempo.
    vaciarFicha: function() {
      this.ficha = null;
      this.desglose = {};
      this.tpcOriginal = {};
      this.infPend = {};
      this.infPendMeta = {};
      // #45 · Y el testigo de que la columna estaba cargada: si no, tras un fallo o un plazo
      // vencido el botón seguiría encendido y `asegurarInfPend()` se saltaría la petición
      // creyendo que ya está, dejando la columna vacía para siempre.
      this.infPendCargado = false;
    },
    // #85 · Con qué mes se ARRANCA algo, o `null` si ahora mismo no hay ninguno del que fiarse.
    // Además de que la ficha y el selector cuadren, exige que no haya otra carga en vuelo: la
    // acción que empieza ahora no puede apoyarse en una ficha que está a punto de ser sustituida,
    // y la botonera tampoco se ve mientras carga.
    //
    // Para lo que ocurre DESPUÉS —una respuesta que llega, un diálogo que se confirma— la pregunta
    // no es esta sino `esElMismoMes`.
    refFicha: function() {
      if (this.cargando) return null;
      return this.mesEnPantalla();
    },
    // #85 · Qué mes hay debajo, se esté cargando algo o no. Una referencia que mezcle el id de un
    // mes con el año y el mes de otro mandaba «+ Persona», Guardar, Quitar o los días al mes
    // anterior. Desde la #769 el carril tira la ficha en cuanto se pide otro mes, así que esa
    // mezcla ya no se pinta; la comprobación se queda porque es lo que protege a las acciones cuya
    // respuesta llega después, y lo que la delata —que el id y el selector dejen de coincidir— se
    // ve aquí sin mirar `cargando`.
    //
    // El año y el mes se cogen de la ficha cargada y no del selector: es el servidor quien dice de
    // qué mes es lo que se está pintando. Mismo criterio en Control de cifras OEB.
    mesEnPantalla: function() {
      if (!this.ficha || !this.ficha.id) return null;
      // La comparación solo se hace si la ficha trae su mes: una API sin este dato no puede dejar
      // la pantalla entera sin botones.
      if (this.ficha.anio != null && this.ficha.mes != null
          && (this.ficha.anio !== this.anio || this.ficha.mes !== this.mes)) return null;
      return { id: this.ficha.id, anio: this.anio, mes: this.mes };
    },
    mismaRef: function(ref, actual) {
      return !!ref && !!actual && actual.id === ref.id
        && actual.anio === ref.anio && actual.mes === ref.mes;
    },
    // #85 · Se sigue en el MISMO mes, aunque haya otra carga de ese mes en vuelo. Para después de
    // una petición, `cargando` no responde a la pregunta que toca: dice si la pantalla está
    // ocupada, no si se ha cambiado de mes. Confundirlos tiraba respuestas del mes correcto: quien
    // añadía a alguien y pulsaba «Actualizar cifras» sin esperar se quedaba sin el refresco de
    // después del alta, y en pantalla la persona no aparecía.
    //
    // Aquí todas las acciones terminan recargando la ficha, y esa recarga anula la que estuviera en
    // vuelo, así que con esto basta: no hay ninguna que pinte la respuesta directamente.
    esElMismoMes: function(ref) {
      return this.mismaRef(ref, this.mesEnPantalla());
    },
    // Lo que no se hace porque la pantalla está cargando otro mes se dice, en vez de dejar el clic
    // sin respuesta.
    avisarFichaEnVuelo: function(titulo) {
      this.$f7.dialog.alert('La pantalla está cargando el mes que has elegido. Espera a que termine y vuelve a intentarlo.', titulo);
    },
    // La lista de «+ Persona» es de un mes concreto, así que al recargar se cierra en vez de
    // quedarse abierta encima del mes nuevo esperando a que alguien pulse y se lleve el rechazo.
    cerrarListaPersonas: function() {
      if (this._listaPersonas) this._listaPersonas.close();
    },
    mesPrevio: function() {
      if (this.mes === 1) { this.mes = 12; this.anio--; } else { this.mes--; }
      this.cargarFicha();
    },
    mesSiguiente: function() {
      if (this.mes === 12) { this.mes = 1; this.anio++; } else { this.mes++; }
      this.cargarFicha();
    },
    actualizarCifras: function() {
      var self = this;
      if (!self.ficha) return;
      // #46 · El refresco entra por el mismo testigo que la carga. Si mientras está en vuelo se
      // cambia de mes, manda la carga nueva y este final —salga bien o mal— ya no toca la
      // pantalla: si no, un refresco fallido del mes anterior apagaba el reloj del mes que se
      // está cargando y dejaba la ficha vieja debajo del selector nuevo como si fuera la suya.
      const nombre = self.nombreMes + ' ' + self.anio;
      // Si la actualización falla, el preloader se quedaba girando sin fin y sin decir nada.
      // Aquí no se pinta el aviso de carga: la ficha que se está viendo sigue siendo válida,
      // lo que no ha ido es el refresco.
      //
      // #164 · El aviso sale AUNQUE se haya cambiado de mes por el camino: lo que ha fallado es
      // una escritura que el usuario pidió, y callársela porque esta operación ya no manda es el
      // fallo silencioso que esta tarea viene a quitar. Por eso lleva el mes dentro —se puede
      // leer con otro mes delante— y sale también por `descartada`; el «cargando» sí es de quien
      // manda, y lo lleva el carril.
      const avisarFallo = function() {
        self.$f7.toast.create({ text: 'No se han podido actualizar las cifras de ' + nombre + '. Vuelve a intentarlo.', position: 'top', closeTimeout: 4000 }).open();
      };
      self.carrilFicha.lanzar(Oeb.actualizarCifras(self.anio, self.mes), {
        exito: function() { self.cargarFicha(); },
        fallo: avisarFallo,
        descartada: function(ok) { if (!ok) avisarFallo(); }
      });
    },
    crearFichaMes: function() {
      var self = this;
      var meses = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      // #85 · El mes se fija al empezar. Son tres peticiones encadenadas y el selector se puede
      // mover por el camino: leyendo `self.anio`/`self.mes` en cada paso se acababa recalculando
      // y actualizando un mes distinto del que se acaba de crear.
      var anio = self.anio;
      var mes = self.mes;
      // #85 · La creación entra por el mismo testigo que la carga. Si mientras está en vuelo se
      // cambia de mes, manda la carga nueva y este final —salga bien o mal— ya no toca el reloj:
      // apagar `cargando` desde aquí sacaba la botonera encima del mes viejo antes de que
      // terminara la carga del nuevo.
      var creada = false;
      const creacion = Oeb.crearFicha(anio, mes).then(function() {
        creada = true;
        return Oeb.recalcularFicha(anio, mes);
      }).then(function() {
        return Oeb.actualizarCifras(anio, mes);
      });
      // #164 · El fallo se avisa siempre; lo que depende de seguir mandando es si además se
      // puede tocar la pantalla. Ver `avisarCreacionFallida`.
      self.carrilFicha.lanzar(creacion, {
        exito: function() {
          self.$f7.toast.create({ text: 'Ficha creada y actualizada', position: 'top', closeTimeout: 2000 }).open();
          self.cargarFicha();
        },
        fallo: function(err) { self.avisarCreacionFallida(err, creada, anio, mes, meses[mes] + ' ' + anio, true); },
        descartada: function(ok, err) {
          if (!ok) self.avisarCreacionFallida(err, creada, anio, mes, meses[mes] + ' ' + anio, false);
        }
      });
    },
    crearMesSiguiente: function() {
      var self = this;
      var mesNuevo = self.mes === 12 ? 1 : self.mes + 1;
      var anioNuevo = self.mes === 12 ? self.anio + 1 : self.anio;
      var meses = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      self.$f7.dialog.confirm('\u00bfCrear ficha de ' + meses[mesNuevo] + ' ' + anioNuevo + '?', 'Crear mes', function() {
        // #85 · Por el mismo carril que la carga, por lo mismo que en `crearFichaMes`.
        var creada = false;
        const nombre = meses[mesNuevo] + ' ' + anioNuevo;
        const creacion = Oeb.crearFicha(anioNuevo, mesNuevo).then(function() {
          creada = true;
          return Oeb.recalcularFicha(anioNuevo, mesNuevo);
        }).then(function() {
          return Oeb.actualizarCifras(anioNuevo, mesNuevo);
        });
        self.carrilFicha.lanzar(creacion, {
          exito: function() {
            self.mes = mesNuevo;
            self.anio = anioNuevo;
            self.$f7.toast.create({ text: 'Ficha de ' + nombre + ' creada', position: 'top', closeTimeout: 2000 }).open();
            self.cargarFicha();
          },
          fallo: function(err) { self.avisarCreacionFallida(err, creada, anioNuevo, mesNuevo, nombre, true); },
          descartada: function(ok, err) {
            if (!ok) self.avisarCreacionFallida(err, creada, anioNuevo, mesNuevo, nombre, false);
          }
        });
      });
    },
    // #85 · Las tres peticiones de la creación caen en el mismo `catch`, pero solo la primera
    // decide si el mes existe: si `crearFicha` ya respondió bien, el servidor conserva la ficha
    // aunque falle el cálculo, y decir «No se ha creado el mes» invita a crearlo otra vez y a
    // llevarse el error de duplicado. Cuando la creación se confirmó —o se cortó sin respuesta, y
    // entonces no se sabe— se lleva el selector a ese mes y se recarga, que es lo único que
    // enseña si está o no. El reloj no se toca aquí (#769): lo apaga el carril cuando no queda
    // nada en vuelo, y si este final recarga, la recarga lo mantiene puesto. Mismo criterio en
    // Control de cifras OEB.
    //
    // #164 · `manda` es si la operación sigue siendo la dueña de la pantalla. Si el usuario cambió
    // de mes mientras la creación viajaba, ya no lo es: el aviso SALE IGUAL —el mes quedó a medias
    // en el servidor y callarlo es justo lo que lleva al duplicado— pero no se le secuestra el
    // selector ni se le pisa la carga que acaba de arrancar, así que el texto le dice que entre él
    // al mes en vez de prometerle que se abre solo.
    avisarCreacionFallida: function(err, creada, anio, mes, nombre, manda) {
      if (!creada && !Oeb.esResultadoIncierto(err)) {
        this.$f7.dialog.alert('No se ha podido crear la ficha de ' + nombre + '. Vuelve a intentarlo.', 'No se ha creado el mes');
        return;
      }
      if (!manda) {
        this.$f7.dialog.alert(creada
          ? 'La ficha de ' + nombre + ' sí se ha creado, pero no se han podido calcular sus cifras. Entra en ese mes y usa «Actualizar cifras» para terminarla: no la crees otra vez.'
          : 'No se ha podido saber si la ficha de ' + nombre + ' llegó a crearse. Entra en ese mes para comprobarlo: si ya está, no la crees otra vez.',
          creada ? 'El mes se ha creado sin cifras' : 'Puede que el mes se haya creado');
        return;
      }
      this.anio = anio;
      this.mes = mes;
      this.$f7.dialog.alert(creada
        ? 'La ficha de ' + nombre + ' sí se ha creado, pero no se han podido calcular sus cifras. Se abre a continuación: no la crees otra vez, usa «Actualizar cifras» para terminarla.'
        : 'No se ha podido saber si la ficha de ' + nombre + ' llegó a crearse. Se abre a continuación para que lo compruebes: si ya está, no la crees otra vez.',
        creada ? 'El mes se ha creado sin cifras' : 'Puede que el mes se haya creado');
      this.cargarFicha();
    },
    // #34 \u00b7 La persona tiene que existir ya en la Configuraci\u00f3n OEB: desde aqu\u00ed no se da de alta
    // a nadie.
    // #85 · Quién se ofrece lo decide el servidor: los activos en la Configuración OEB del AÑO de
    // este mes que todavía no se ven aquí. Esta pantalla lo montaba cruzando la lista de personas
    // —que no tiene año— con las filas pintadas, y así personas activas en la configuración del
    // ejercicio no llegaban a salir en el desplegable.
    // La lista se pide para UNA ficha concreta y solo vale para ella: el año y el mes se pueden
    // cambiar mientras llega la respuesta, y entonces lo que se pintaría es la gente de un mes
    // sobre el selector de otro. Por eso se guarda el mes con el que se pidió —ficha Y selector,
    // ver `refFicha`— y se comprueba al llegar, y otra vez al pulsar cada botón, que ocurre más
    // tarde todavía.
    agregarPersona: function() {
      var self = this;
      var ref = self.refFicha();
      if (!ref) return self.avisarFichaEnVuelo('No se ha añadido a nadie');
      var anioPedido = ref.anio;
      Oeb.getPersonasDisponiblesParaFicha(ref.id)
        .then(function(res) {
          if (!self.esElMismoMes(ref)) return;
          var disponibles = unwrapDto(res) || [];
          if (disponibles.length === 0) {
            self.$f7.dialog.alert('Todas las personas activas en la Configuración OEB de ' + anioPedido + ' ya están en esta pantalla. Para incorporar a alguien nuevo, primero hay que darle de alta en Configuración OEB de ' + anioPedido + '.', 'Sin personas que añadir');
            return;
          }
          var buttons = disponibles.map(function(p) {
            // «vuelve» y no «se añade»: tiene entrada en el mes porque se la quitaron, y al
            // ponerla otra vez recupera sus cifras en vez de empezar de cero (#34).
            return {
              text: p.codigoPersonal
                + (p.nombre && p.nombre !== p.codigoPersonal ? ' — ' + p.nombre : '')
                + (p.quitadaDelMes ? ' (vuelve con sus cifras)' : ''),
              onClick: function() { self.agregarPersonaCodigo(p.codigoPersonal, ref); }
            };
          });
          self.cerrarListaPersonas();
          self._listaPersonas = self.$f7.actions.create({
            buttons: [
              [{ text: 'Personas de la Configuración OEB ' + anioPedido, label: true }].concat(buttons),
              [{ text: 'Cancelar' }]
            ],
            on: {
              closed: function() {
                if (self._listaPersonas === this) self._listaPersonas = null;
                this.destroy();
              }
            }
          });
          self._listaPersonas.open();
        })
        .catch(function() {
          // El fallo también es del mes con el que se pidió la lista: si la pantalla ya está en
          // otro, el aviso hablaría de una petición que quien lo lee no ha hecho desde aquí.
          if (!self.esElMismoMes(ref)) return;
          self.$f7.dialog.alert('No se ha podido cargar la lista de personas de la Configuración OEB', 'Error');
        });
    },
    // `refEsperada` es el mes del que salió la lista: si la pantalla ya está en otro se rechaza en
    // vez de añadir a ciegas, que añadiría al mes anterior mientras el selector enseña el nuevo.
    agregarPersonaCodigo: function(codigo, refEsperada) {
      var self = this;
      if (!refEsperada || !self.esElMismoMes(refEsperada)) {
        self.$f7.dialog.alert('La pantalla ha cambiado de mes mientras la lista estaba abierta. Vuelve a pulsar «+ Persona».', 'No se ha añadido a nadie');
        return;
      }
      // El POST va al id de la referencia, no a `ficha.id`: es el mes del que salió la lista.
      Oeb.agregarPersonaAFicha(refEsperada.id, codigo).then(function(res) {
        if (!self.esElMismoMes(refEsperada)) return;
        if (unwrapDto(res)) {
          self.$f7.toast.create({ text: codigo + ' a\u00f1adido', position: 'top', closeTimeout: 2000 }).open();
          self.cargarFicha();
        } else {
          self.$f7.dialog.alert(res.message || 'Error', 'Error');
        }
      }).catch(function() {
        if (!self.esElMismoMes(refEsperada)) return;
        self.$f7.dialog.alert('No se ha podido a\u00f1adir a ' + codigo + ' a este mes', 'Error');
      });
    },
    // #34 \u00b7 Fija para este mes la lista de personas que hay ahora en pantalla. A partir de aqu\u00ed
    // la ve cualquier usuario que abra esta pantalla \u2014y Control de cifras OEB, que comparte
    // ficha\u2014 en este mes, y el mes siguiente la hereda al crearse.
    //
    // La lista que se guarda es la del mes que se estaba mirando al pulsar, no la del que haya
    // cuando se confirme: entre una cosa y otra cabe un cambio de mes, y guardar entonces fijaría
    // en el mes nuevo las personas del anterior.
    guardarPersonasVisibles: function() {
      var self = this;
      if (!self.ficha || self.guardandoPersonas) return;
      var ref = self.refFicha();
      if (!ref) return self.avisarFichaEnVuelo('No se ha guardado');
      var codigos = (self.ficha.personas || []).map(function(p) { return p.codigoPersonal; });
      self.$f7.dialog.confirm(
        'Se fijan las ' + codigos.length + ' personas que se ven ahora para ' + self.nombreMes + ' ' + self.anio + '. Las ver\u00e1 as\u00ed todo el mundo, tambi\u00e9n en Control de cifras OEB. \u00bfGuardar?',
        'Guardar personas del mes',
        function() {
          if (!self.esElMismoMes(ref)) {
            self.$f7.dialog.alert('La pantalla ha cambiado de mes. No se ha guardado nada: vuelve a pulsar «Guardar personas del mes».', 'No se ha guardado');
            return;
          }
          cerrojoDeLaSesion(self, 'guardandoPersonas');
          Oeb.guardarPersonasVisibles(ref.id, codigos)
            .then(function() {
              self.guardandoPersonas = false;
              // El aviso y el refresco son del mes que se guardó. El texto del toast se arma con
              // `nombreMes`, que ya es el del selector nuevo: sin esta comprobación anunciaba
              // guardado un mes en el que no se ha tocado nada.
              if (!self.esElMismoMes(ref)) return;
              self.$f7.toast.create({ text: 'Personas de ' + self.nombreMes + ' guardadas', position: 'top', closeTimeout: 2000 }).open();
              self.cargarFicha();
            })
            .catch(function() {
              self.guardandoPersonas = false;
              if (!self.esElMismoMes(ref)) return;
              self.$f7.dialog.alert('No se han podido guardar las personas del mes', 'Error');
            });
        });
    },
    irANotificaciones: function() {
      this.$f7router.navigate('/dashboard/oeb/notificaciones/');
    },
    enviarReporte: function() {
      var self = this;
      var mesNombre = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      self.reporteAsunto = 'Informe OEB Planificacion — SIMECAL — ' + mesNombre[self.mes] + ' ' + self.anio;
      Oeb.getPersonasConfig().then(function(res) {
        var personas = unwrapDto(res) || [];
        var emails = personas.filter(function(p) { return p.email && p.activo; }).map(function(p) { return p.email; });
        self.reporteEmails = emails.join(', ');
        self.popupReporte = true;
      }).catch(function() { self.popupReporte = true; });
    },
    actualizarAsuntoReporte: function() {
      var mesNombre = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      if (this.reporteTipo === 'DIARIO') this.reporteAsunto = 'Informe diario OEB Plan — SIMECAL — ' + new Date().toLocaleDateString('es-ES');
      else if (this.reporteTipo === 'SEMANAL') this.reporteAsunto = 'Informe semanal OEB Plan — SIMECAL';
      else this.reporteAsunto = 'Informe OEB Planificacion — SIMECAL — ' + mesNombre[this.mes] + ' ' + this.anio;
    },
    confirmarEnvioReporte: function() {
      var self = this;
      var emails = self.reporteEmails.split(',').map(function(e) { return e.trim(); }).filter(function(e) { return e; });
      if (!emails.length) {
        self.$f7.toast.create({ text: 'Introduce al menos un email', position: 'top', closeTimeout: 2000 }).open();
        return;
      }
      var emailsCc = self.reporteCc ? self.reporteCc.split(',').map(function(e) { return e.trim(); }).filter(function(e) { return e; }) : [];
      cerrojoDeLaSesion(self, 'enviandoReporte');
      Oeb.enviarReporte({
        anio: self.anio, mes: self.mes,
        emails: emails, emailsCc: emailsCc, emailsCco: [],
        asunto: self.reporteAsunto, tipoReporte: self.reporteTipo, origen: 'PLANIFICACION'
      }).then(function(res) {
        self.enviandoReporte = false;
        self.popupReporte = false;
        self.$f7.toast.create({ text: unwrapDto(res) || 'Reporte enviado', position: 'top', closeTimeout: 3000 }).open();
      }).catch(function(e) {
        self.enviandoReporte = false;
        self.$f7.toast.create({ text: 'Error: ' + (e.message || 'desconocido'), position: 'top', closeTimeout: 3000 }).open();
      });
    },
    // === DATA ACCESS ===
    // C5: suma de un campo del desglose (oebR/oebP/oebPPend) en todas las semanas del mes
    sumaDesglosePersona: function(codigo, campo) {
      var self = this;
      var persona = (self.ficha && self.ficha.personas ? self.ficha.personas : [])
        .find(function(p) { return p.codigoPersonal == codigo; });
      var nSemanas = persona && persona.semanas ? persona.semanas.length : 0;
      var total = 0;
      for (var i = 0; i < nSemanas; i++) {
        total += self.getDesgloseVal(codigo, campo, i) || 0;
      }
      return total;
    },
    // C5: abre la ficha de desempeño del inspector (SOLO LECTURA; roles fase 1 vía v-roles + backend)
    abrirDesempeno: function(persona) {
      var self = this;
      var app = self.$f7;
      self.desempeno = null;
      cerrojoDeLaSesion(self, 'desempenoLoading');
      self.popupDesempeno = true;
      Desempeno.ficha({ data: { codigoPersonal: persona.codigoPersonal, anio: self.anio, mes: self.mes } })
        .then(function(res) {
          self.desempeno = res && res.dto ? res.dto : ((res && res.data) ? res.data : res);
        })
        .catch(function(e) {
          if (typeof e == "string") e = { error: e };
          app.dialog.alert(e.message || e.error || 'No se pudo cargar la ficha de desempeño.');
          self.popupDesempeno = false;
        })
        .finally(function() {
          self.desempenoLoading = false;
        });
    },
    getDesgloseVal: function(codigo, campo, idx) {
      if (!this.desglose || !this.desglose[codigo]) return 0;
      var arr = this.desglose[codigo][campo];
      return arr && arr[idx] != null ? arr[idx] : 0;
    },
    getTpcVal: function(codigo, idx) {
      if (!this.tpcOriginal || !this.tpcOriginal[codigo]) return 0;
      var arr = this.tpcOriginal[codigo];
      return arr && arr[idx] != null ? arr[idx] : 0;
    },
    // === INF.PEND. ===
    // null significa "no hay dato para esa semana" (semana futura), y se pinta en blanco.
    // Un 0 significa "no le queda nada pendiente", que es otra cosa: no se mezclan.
    getInfPendVal: function(codigo, idx) {
      if (!this.infPend || !this.infPend[codigo]) return null;
      var arr = this.infPend[codigo];
      return arr && arr[idx] != null ? arr[idx] : null;
    },
    // Una semana futura sale en blanco para todo el mundo: sin dato tampoco se pinta el punto de
    // color, que si no parece una celda con un valor que no ha cargado.
    infPendConDato: function(idx) {
      var tipos = this.infPendMeta && this.infPendMeta.tipoSemana;
      return !!(tipos && tipos[idx] && tipos[idx] !== 'FUTURA');
    },
    infPendSemTexto: function(persona, idx) {
      var v = this.getInfPendVal(persona.codigoPersonal, idx);
      if (v == null) return '';
      return this.fmtCelda(v);
    },
    totalInfPendSemTexto: function(idx) {
      var t = this.semTotales[idx];
      if (!t || t.infPend == null) return '';
      return this.fmtCelda(t.infPend);
    },
    // Qué corte representa la celda. Sin esto, dos columnas con el mismo aspecto significan cosas
    // distintas —una es una foto congelada y la otra el dato de hoy— y no hay forma de saberlo.
    infPendTitulo: function(idx) {
      var tipos = this.infPendMeta && this.infPendMeta.tipoSemana;
      var tipo = tipos && tipos[idx];
      if (tipo === 'ACTUAL') return 'INF.PEND. a día de hoy (semana en curso, el valor todavía se mueve)';
      if (tipo === 'PASADA') return 'INF.PEND. el domingo de esa semana a las 23:59 — no cambia porque hoy se emitan informes; sí cambia si se corrige el importe o el reparto de una línea';
      return 'Semana futura: todavía no hay dato';
    },
    // === DETAIL COLUMNS ===
    faltaSemVal: function(persona, idx) {
      var obj = persona.objetivosSemana && persona.objetivosSemana[idx] ? persona.objetivosSemana[idx] : 0;
      var res = persona.resultadosSemana && persona.resultadosSemana[idx] ? persona.resultadosSemana[idx] : 0;
      return obj - res;
    },
    faltaSemTexto: function(persona, idx) {
      var f = this.faltaSemVal(persona, idx);
      if (Math.abs(f) < 1) return '\u2014';
      if (f < 0) return '+' + this.fmtCelda(Math.abs(f));
      return '-' + this.fmtCelda(f);
    },
    faltaSemClase: function(persona, idx) {
      return this.faltaSemVal(persona, idx) <= 0 ? 'det-verde' : 'det-rojo';
    },
    totalFaltaSemVal: function(idx) {
      if (!this.ficha || !this.ficha.totales || !this.ficha.totales.porSemana) return 0;
      var s = this.ficha.totales.porSemana[idx];
      return s ? (s.objetivoSemana || 0) - (s.resultadoSemana || 0) : 0;
    },
    totalFaltaSemTexto: function(idx) {
      var f = this.totalFaltaSemVal(idx);
      if (Math.abs(f) < 1) return '\u2014';
      if (f < 0) return '+' + this.fmtCelda(Math.abs(f));
      return '-' + this.fmtCelda(f);
    },
    totalFaltaSemClase: function(idx) {
      return this.totalFaltaSemVal(idx) <= 0 ? 'det-verde' : 'det-rojo';
    },
    // === TOGGLE STYLE ===
    toggleStyle: function(color, activo) {
      if (activo) return { background: color, color: '#fff', border: '2px solid ' + color };
      return { background: 'transparent', color: color, border: '2px solid ' + color };
    },
    // === FORMATTING ===
    fmtCelda: function(valor) {
      if (valor == null || Math.abs(valor) < 0.5) return '\u2014';
      return Math.round(valor).toLocaleString('es-ES');
    },
    formatImporte: function(valor) {
      if (valor == null) return '0 \u20AC';
      return Math.round(valor).toLocaleString('es-ES') + ' \u20AC';
    },
    formatImporteCorto: function(valor) {
      if (valor == null) return '0';
      return Math.round(valor).toLocaleString('es-ES');
    },
    formatImporteConSigno: function(valor) {
      if (valor == null) return '0 \u20AC';
      var signo = valor >= 0 ? '+' : '';
      return signo + Math.round(valor).toLocaleString('es-ES') + ' \u20AC';
    },
    formatPorcentaje: function(valor) {
      if (valor == null) return '0%';
      return valor.toFixed(1) + '%';
    },
    formatFecha: function(fecha) {
      if (!fecha) return '';
      var d = new Date(fecha);
      return d.toLocaleDateString('es-ES') + ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    },
    extraerDias: function(semana) {
      if (!semana || !semana.etiqueta) return '';
      var match = semana.etiqueta.match(/\((\d+-\d+)\)/);
      return match ? match[1] : '';
    },
    colorProgresion: function(persona) {
      var r = persona.progresionMes || 0;
      var o = persona.objetivoMensual || 0;
      if (!o || o <= 0 || !r) return {};
      var p = (r / o) * 100;
      if (p >= 105) return { background: '#D6EAF8', color: '#1B4F72' };
      if (p >= 100) return { background: '#D5F5E3', color: '#0E6251' };
      if (p >= 95) return { background: '#FADBD8', color: '#943126' };
      return { background: '#F1948A', color: '#641E16' };
    },
    calcularEstadoColor: function(porcentaje) {
      if (porcentaje == null) return 'MUY_MALO';
      if (porcentaje > 105) return 'MUY_BUENO';
      if (porcentaje >= 100) return 'BUENO';
      if (porcentaje >= 95) return 'MALO';
      return 'MUY_MALO';
    },
    // === DISPONIBILIDAD HORAS ===
    getMinutosPlan: function(codigo, idx) {
      if (!this.desglose || !this.desglose[codigo] || !this.desglose[codigo].minutosPlanificados) return 0;
      return this.desglose[codigo].minutosPlanificados[idx] || 0;
    },
    dispMinutos: function(persona, idx) {
      var diasHab = persona.semanas && persona.semanas[idx] ? (persona.semanas[idx].diasHabiles || 0) : 0;
      var plan = this.getMinutosPlan(persona.codigoPersonal, idx);
      return (diasHab * 480) - plan;
    },
    formatMinHM: function(min) {
      if (min == null || min <= 0) return '0h 00';
      var h = Math.floor(min / 60);
      var m = min % 60;
      return h + 'h ' + (m < 10 ? '0' : '') + m;
    },
    formatMinHMSigned: function(min) {
      if (min == null || min === 0) return '0h 00';
      if (min < 0) {
        var abs = Math.abs(min);
        var h = Math.floor(abs / 60);
        var m = abs % 60;
        return '\u2212' + h + 'h ' + (m < 10 ? '0' : '') + m;
      }
      return this.formatMinHM(min);
    },
    dispHorasTexto: function(persona, idx) {
      var disp = this.dispMinutos(persona, idx);
      return this.formatMinHMSigned(disp);
    },
    dispHorasClase: function(persona, idx) {
      var disp = this.dispMinutos(persona, idx);
      if (disp >= 1200) return 'disp-verde';
      if (disp >= 480) return 'disp-naranja';
      return 'disp-rojo';
    },
    abrirDispHoras: function(persona, idx) {
      this.dispPersona = persona;
      this.dispSemIdx = idx;
      this.popupDisp = true;
    },
    dispSemPrev: function() {
      if (this.dispSemIdx > 0) this.dispSemIdx--;
    },
    dispSemNext: function() {
      if (!this.ficha || !this.ficha.calendario) return;
      if (this.dispSemIdx < this.ficha.calendario.semanas.length - 1) this.dispSemIdx++;
    },
    // === FALTA TPC(SI) ===
    sumaTpcPersona: function(codigo) {
      if (!this.tpcOriginal || !this.tpcOriginal[codigo]) return 0;
      return this.tpcOriginal[codigo].reduce(function(s, v) { return s + (v || 0); }, 0);
    },
    faltaTpcVal: function(persona) {
      var tpc = this.sumaTpcPersona(persona.codigoPersonal);
      var oeb = persona.progresionMes || 0;
      return tpc - oeb;
    },
    faltaTpcTexto: function(persona) {
      var diff = this.faltaTpcVal(persona);
      if (Math.abs(diff) < 0.5) return '0';
      return this.formatImporteConSigno(diff);
    },
    faltaTpcClase: function(persona) {
      var diff = this.faltaTpcVal(persona);
      if (diff >= 0) return 'valor-positivo';
      return 'valor-negativo';
    },
    // === ΔTPC POR CELDA DE SEMANA ===
    difTpcSemVal: function(persona, idx) {
      var tpc = this.getTpcVal(persona.codigoPersonal, idx);
      var oeb = persona.resultadosSemana && persona.resultadosSemana[idx] ? persona.resultadosSemana[idx] : 0;
      return tpc - oeb;
    },
    difTpcSemTexto: function(persona, idx) {
      var diff = this.difTpcSemVal(persona, idx);
      if (Math.abs(diff) < 0.5) return '\u2014';
      return this.fmtCelda(diff);
    },
    difTpcSemClase: function(persona, idx) {
      var diff = this.difTpcSemVal(persona, idx);
      if (diff >= 0) return 'dif-tpc-pos';
      return 'dif-tpc-neg';
    },
    totalDifTpcSemTexto: function(idx) {
      var t = this.semTotales[idx];
      if (!t) return '\u2014';
      var diff = (t.tpc || 0) - (t.oeb || 0);
      if (Math.abs(diff) < 0.5) return '\u2014';
      return this.fmtCelda(diff);
    },
    totalDifTpcSemClase: function(idx) {
      var t = this.semTotales[idx];
      if (!t) return '';
      return ((t.tpc || 0) - (t.oeb || 0)) >= 0 ? 'dif-tpc-pos' : 'dif-tpc-neg';
    },
    // === POPUP DESGLOSE DIARIO ===
    abrirDesgloseDiario: function(persona, semIdx) {
      var cod = persona.codigoPersonal;
      var sem = this.ficha && this.ficha.calendario && this.ficha.calendario.semanas
          ? this.ficha.calendario.semanas[semIdx] : null;
      this.desgloseCod = cod;
      this.desgloseSemNum = sem ? sem.numero : 0;
      var desg = this.desglose && this.desglose[cod] && this.desglose[cod].detalleDiario
          ? this.desglose[cod].detalleDiario[semIdx] : null;
      this.desgloseSemIdx = semIdx;
      this.desgloseDias = desg ? desg.slice().sort(function(a, b) { return (a.dia || 0) - (b.dia || 0); }) : [];
      this.popupDesglose = true;
    },
    diaSemanaLabel: function(dia) {
      if (!dia || !this.anio || !this.mes) return '';
      var d = new Date(this.anio, this.mes - 1, dia);
      var dias = ['Dom', 'Lun', 'Mar', 'Mi\u00e9', 'Jue', 'Vie', 'S\u00e1b'];
      return dias[d.getDay()];
    },
    getDiasRealesSem: function(cod, idx) {
      if (!this.desglose || !this.desglose[cod] || !this.desglose[cod].diasRealesSem) return 0;
      return this.desglose[cod].diasRealesSem[idx] != null ? this.desglose[cod].diasRealesSem[idx] : 0;
    },
    getColorDiaSemana: function(cod, idx) {
      var aus = this.ausencias[cod];
      if (!aus || !this.ficha || !this.ficha.calendario) return '';
      var sem = this.ficha.calendario.semanas[idx];
      if (!sem) return '';
      var iniParts = sem.fechaInicio.split('-'); var finParts = sem.fechaFin.split('-');
      var dIni = parseInt(iniParts[2]); var dFin = parseInt(finParts[2]);
      var mIni = parseInt(iniParts[1]); var mFin = parseInt(finParts[1]);
      var hasBaja = false, hasOtra = false;
      for (var i = 0; i < aus.length; i++) {
        var d = aus[i].dia;
        var enSem = (mIni === this.mes && mFin === this.mes) ? (d >= dIni && d <= dFin) :
                    (mIni < this.mes ? d <= dFin : d >= dIni);
        if (!enSem) continue;
        if (aus[i].motivo && aus[i].motivo.toUpperCase().indexOf('BAJA') >= 0) hasBaja = true;
        else hasOtra = true;
      }
      if (hasBaja) return 'dia-baja';
      if (hasOtra) return 'dia-ausencia';
      return '';
    },
    getHorasDiarias: function(cod) {
      if (this.horasDiariasData[cod] != null) return this.horasDiariasData[cod];
      return this.getNumIc(cod) * 8;
    },
    guardarHorasDiarias: function(cod, valor) {
      var hd = parseFloat(valor);
      if (isNaN(hd)) return;
      this.$set(this.horasDiariasData, cod, hd);
    },
    getProdHora: function(cod) {
      if (!this.ficha || !this.ficha.personas) return 0;
      var p = this.ficha.personas.find(function(pp) { return pp.codigoPersonal === cod; });
      var dr = p ? (p.diasReales || 0) : 0;
      if (dr <= 0) return 0;
      var prodDia = this.sumaTpcPersona(cod) / dr;
      var hd = this.getHorasDiarias(cod);
      return hd > 0 ? Math.round(prodDia / hd) : 0;
    },
    getNumIc: function(cod) {
      return this.numIcData[cod] != null ? this.numIcData[cod] : 1;
    },
    guardarNumIc: function(cod, valor) {
      var numIc = parseFloat(valor);
      if (isNaN(numIc)) return;
      this.$set(this.numIcData, cod, numIc);
      // Si H.D. no fue editado manualmente, se recalcula automáticamente via getHorasDiarias (reactivo)
    },
    abrirGuiaPdf: function() {
      window.open('/static/docs/SIMECAL_Guia_OEB_Planificacion.pdf', '_blank');
    },
    cargarInstrucciones: function() {
      var self = this;
      Oeb.getInstrucciones('control-cifras-oeb-planificacion').then(function(res) {
        self.instrucciones = (unwrapDto(res) || []).map(function(i) { i._editando = false; return i; });
      }).catch(function() {});
    },
    nuevaInst: function() {
      var self = this;
      Oeb.crearInstruccion({ pantalla: 'control-cifras-oeb-planificacion', titulo: 'Nueva instrucción', texto: 'Escribe aquí...', orden: self.instrucciones.length }).then(function(res) {
        if (unwrapDto(res)) { unwrapDto(res)._editando = true; self.instrucciones.push(unwrapDto(res)); self.expandedInst = unwrapDto(res).id; }
      }).catch(function(err) {
        avisoNoGuardado(self.$f7, 'la instrucción nueva', err);
      });
    },
    guardarInst: function(inst) {
      var self = this;
      Oeb.actualizarInstruccion(inst.id, { titulo: inst.titulo, texto: inst.texto }).then(function() {
        inst._editando = false;
        self.$f7.toast.create({ text: 'Instrucción guardada', position: 'top', closeTimeout: 2000 }).open();
      }).catch(function(err) {
        avisoNoGuardado(self.$f7, 'la instrucción «' + inst.titulo + '»', err);
      });
    },
    eliminarInst: function(inst) {
      var self = this;
      self.$f7.dialog.confirm('¿Eliminar "' + inst.titulo + '"?', 'Eliminar', function() {
        Oeb.eliminarInstruccion(inst.id).then(function() {
          self.instrucciones = self.instrucciones.filter(function(i) { return i.id !== inst.id; });
        }).catch(function(err) {
          avisoNoGuardado(self.$f7, 'el borrado de «' + inst.titulo + '»', err);
        });
      });
    },
    // El orden se reordena en pantalla antes de que el servidor conteste, así que si el guardado
    // falla hay que decirlo y recargar: dejar la lista movida sin que lo esté en el servidor hace
    // creer que el orden nuevo es el que verá el siguiente que entre. Esta recarga vale porque las
    // mutaciones de instrucciones tiran la caché de 60 s del adapter; si no, restauraría la foto
    // anterior a los cambios que sí se guardaron en ese minuto.
    dropInst: function(idx) {
      const self = this;
      var from = this.draggingInst;
      if (from === null || from === idx) return;
      var item = this.instrucciones.splice(from, 1)[0];
      this.instrucciones.splice(idx, 0, item);
      this.draggingInst = null;
      Oeb.reordenarInstrucciones(this.instrucciones.map(function(i) { return i.id; }))
        .catch(function(err) {
          avisoNoGuardado(self.$f7, 'el orden de las instrucciones', err);
          self.cargarInstrucciones();
        });
    },
    guardarTodosNumIc: function() {
      var self = this;
      if (!self.ficha || !self.ficha.personas) return;
      cerrojoDeLaSesion(self, 'guardandoIc');
      var promesas = self.ficha.personas.map(function(p) {
        var cod = p.codigoPersonal;
        var body = { codigoPersonal: cod, anio: self.anio, mes: self.mes, numIc: self.getNumIc(cod) };
        if (self.horasDiariasData[cod] != null) body.horasDiarias = self.horasDiariasData[cod];
        return Oeb.guardarNumerosIC(body);
      });
      // #663 · Atado a la sesión: si cambia en vuelo, ni la cola de preferencias ni el toast de
      // «Error al guardar» que dejaba la cancelación caen sobre la cuenta siguiente.
      atarALaSesion(Promise.all(promesas)).then(function() {
        // I/C guardados OK. Ahora persistir toggles y reflejar su resultado real en el toast. Este
        // botón pasa por la misma cola que el guardado automático: si las vistas no se han podido
        // leer, mejor avisar de que no se guardaron que machacar las del usuario con los defaults,
        // y si hay un guardado automático en vuelo este espera a que termine en vez de pisarlo.
        self.encolarGuardadoToggles()
          .then(function() {
            self.guardandoIc = false;
            self.$f7.toast.create({ text: 'I/C y preferencias guardados', position: 'top', closeTimeout: 2000 }).open();
          })
          .catch(function() {
            self.guardandoIc = false;
            self.$f7.toast.create({ text: 'I/C guardado; error al guardar preferencias', position: 'top', closeTimeout: 3000 }).open();
          });
      }).catch(function() {
        self.guardandoIc = false;
        self.$f7.toast.create({ text: 'Error al guardar', position: 'top', closeTimeout: 2000 }).open();
      });
    },
    getDesgloseBL: function(cod) {
      if (!this.desglose || !this.desglose[cod]) return 0;
      return this.desglose[cod].diasBajaLaboral || 0;
    },
    getCompensacionPersona: function(cod) {
      if (!this.compensacionData || !this.compensacionData[cod]) return 0;
      return this.compensacionData[cod];
    },
    getSemanaParaDia: function(dia) {
      if (!this.ficha || !this.ficha.calendario || !this.ficha.calendario.semanas) return '?';
      var sems = this.ficha.calendario.semanas;
      for (var i = 0; i < sems.length; i++) {
        var ini = sems[i].fechaInicio; var fin = sems[i].fechaFin;
        if (!ini || !fin) continue;
        var iniParts = ini.split('-'); var finParts = fin.split('-');
        var dIni = parseInt(iniParts[2]); var dFin = parseInt(finParts[2]);
        var mIni = parseInt(iniParts[1]); var mFin = parseInt(finParts[1]);
        if (mIni === this.mes && mFin === this.mes && dia >= dIni && dia <= dFin) return sems[i].numero;
        if (mIni < this.mes && dia <= dFin) return sems[i].numero;
        if (mFin > this.mes && dia >= dIni) return sems[i].numero;
      }
      return '?';
    },
    getAusenciaDia: function(dia) {
      var aus = this.ausencias[this.desgloseCod];
      if (!aus) return null;
      var item = aus.find(function(a) { return a.dia === dia; });
      return item ? item.motivo : null;
    },
    // === OEB MANUAL OVERRIDE ===
    tieneOverride: function(cod, idx) {
      return this.overrides && this.overrides[cod] && this.overrides[cod][idx] != null;
    },
    abrirManualOverride: function(persona, idx) {
      var sem = this.ficha && this.ficha.calendario && this.ficha.calendario.semanas
          ? this.ficha.calendario.semanas[idx] : null;
      this.manualCod = persona.codigoPersonal;
      this.manualSemIdx = idx;
      this.manualSemNum = sem ? sem.numero : 0;
      var ov = this.overrides && this.overrides[persona.codigoPersonal]
          ? this.overrides[persona.codigoPersonal][idx] : null;
      this.manualValor = ov ? ov.oebManual : '';
      this.manualMotivo = (ov && ov.motivo) ? ov.motivo : '';
      this.manualError = '';
      this.manualInfo = ov || null;
      this.popupManual = true;
    },
    guardarManualOverride: function() {
      var self = this;
      var esBorrado = (self.manualValor === '' || self.manualValor === null || self.manualValor === undefined);
      // Motivo obligatorio SOLO al guardar/modificar (no al borrar)
      if (!esBorrado && (!self.manualMotivo || !self.manualMotivo.trim())) {
        self.manualError = 'Debe indicar el motivo del ajuste manual (auditoría ENAC).';
        return;
      }
      self.manualError = '';
      // #663 · La espera de la sesión: si la sesión cambia en vuelo, la frontera calla el `then` que la apagaba.
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      Oeb.setManualOverride({
        codigoPersonal: self.manualCod,
        anio: self.anio,
        mes: self.mes,
        semanaIdx: self.manualSemIdx,
        oebManual: esBorrado ? null : parseFloat(self.manualValor),
        motivo: esBorrado ? null : self.manualMotivo.trim()
      }).then(function() {
        self.popupManual = false;
        espera.cerrar();
        self.cargarFicha();
      }).catch(function(err) {
        // El popup se queda abierto con el valor escrito: el ajuste manual lleva motivo obligatorio
        // por auditoría ENAC y hacerlo teclear otra vez invita a poner cualquier cosa.
        espera.cerrar();
        avisoNoGuardado(self.$f7, 'el ajuste manual de ' + self.manualCod, err);
      });
    },
    borrarManualOverride: function() {
      var self = this;
      self.manualValor = '';
      self.manualMotivo = '';
      self.manualError = '';
      self.guardarManualOverride();
    },
    formatFechaCorta: function(fecha) {
      if (!fecha) return '';
      var d = new Date(fecha);
      return d.getDate() + '/' + (d.getMonth() + 1) + '/' + d.getFullYear() + ' ' + d.getHours() + ':' + (d.getMinutes() < 10 ? '0' : '') + d.getMinutes();
    },
    // === POPUP ANÁLISIS DIFERENCIAS ===
    abrirAnalisisDiferencias: function(persona, semIdx) {
      var self = this;
      var cod = persona.codigoPersonal;
      // #16 · La clave lleva año y mes: con solo el código, al cambiar de mes el popup reutilizaba
      // el análisis del mes anterior bajo una cabecera que anunciaba el nuevo.
      var clave = self.anio + '-' + self.mes + '-' + cod;
      self.analisisPersonaCod = cod;
      self.analisisSemIdx = semIdx;
      self.popupAnalisis = true;
      // Usar caché si ya se descargó esta persona
      if (self.analisisCache[clave]) {
        self.analisisData = self.analisisCache[clave];
        self.buscarSemanaAnalisis(semIdx);
        return;
      }
      self.analisisData = null;
      Oeb.getAnalisisDiferenciasPorPersona(self.anio, self.mes, cod)
        .then(function(res) {
          if (unwrapDto(res)) {
            self.analisisData = unwrapDto(res);
            self.analisisCache[clave] = unwrapDto(res);
            self.buscarSemanaAnalisis(semIdx);
          }
        })
        .catch(function() {});
    },
    buscarSemanaAnalisis: function(semIdx) {
      if (this.ficha && this.ficha.calendario && this.ficha.calendario.semanas[semIdx] && this.analisisData) {
        var semNum = this.ficha.calendario.semanas[semIdx].numero;
        var semanas = this.analisisData.personas && this.analisisData.personas.length ? this.analisisData.personas[0].semanas || [] : [];
        for (var i = 0; i < semanas.length; i++) {
          if (semanas[i].semana === semNum) { this.analisisSemIdx = i; return; }
        }
      }
    },
    analisisSemPrev: function() { if (this.analisisSemIdx > 0) this.analisisSemIdx--; },
    analisisSemNext: function() {
      if (!this.analisisData || !this.analisisData.personas || !this.analisisData.personas.length) return;
      var semanas = this.analisisData.personas[0].semanas || [];
      if (this.analisisSemIdx < semanas.length - 1) this.analisisSemIdx++;
    },
    navegarPedido: function(pedidoId) {
      this.popupAnalisis = false;
      this.$f7router.navigate('/pedidos/view/' + pedidoId + '/');
    },
    navegarTarea: function(idTarea) {
      this.popupAnalisis = false;
      this.$f7router.navigate('/tarea/view/' + idTarea + '/');
    },
    causaColor: function(causa) {
      var map = {
        'DESFASE_TEMPORAL': '#e6a817', 'TAREA_SIN_PEDIDO': '#7B2D8E', 'PENDIENTE_CONFIRMAR': '#0d6efd',
        'SUMAR_IMPORTE_FALSE': '#888', 'PEDIDO_CANCELADO': '#8B0000',        'DIFERENCIA_IMPORTE': '#17a2b8', 'PLANIFICADA_SIN_EJECUTAR': '#dc3568', 'EJECUTADA_SIN_PLANIFICAR': '#2d5f3a',
        'EJECUTADA_TPC_NO': '#FF6B35', 'TAREA_DOBLE_IMPORTE': '#E91E63',
        'OK': '#2E7D32'
      };
      return map[causa] || '#666';
    },
    causaLabel: function(causa) {
      var map = {
        'DESFASE_TEMPORAL': 'Desfase', 'TAREA_SIN_PEDIDO': 'Tarea s/ped', 'PENDIENTE_CONFIRMAR': 'Pdte.conf',
        'SUMAR_IMPORTE_FALSE': 'NoSuma', 'PEDIDO_CANCELADO': 'Cancelado',        'DIFERENCIA_IMPORTE': 'Dif.importe', 'PLANIFICADA_SIN_EJECUTAR': 'Plan.s/ejec', 'EJECUTADA_SIN_PLANIFICAR': 'Ejec.s/plan',
        'EJECUTADA_TPC_NO': 'Ejec.s/TPC', 'TAREA_DOBLE_IMPORTE': 'Tarea doble',
        'OK': 'OK'
      };
      return map[causa] || causa;
    },
    // === COLUMNAS DÍAS/OBJETIVOS (igual que Producción) ===
    calcularDiasHabilesPersona: function(persona) {
      if (!persona.semanas) return 0;
      return persona.semanas.reduce(function(sum, s) { return sum + (s.diasHabiles || 0); }, 0);
    },
    // === POPUP DETALLE PERSONA ===
    abrirDetallePersona: function(persona) {
      this.personaSel = persona;
      this.objetivoEditable = persona.objetivoDiarioActual || 0;
      this.popupPersona = true;
    },
    // #85 · El objetivo va al mes que se estaba mirando, como el resto de acciones de la pantalla:
    // el popup de la persona se queda abierto por encima del selector, así que aquí también cabe un
    // cambio de mes con la ficha vieja todavía debajo.
    guardarObjetivo: function(persona) {
      var self = this;
      var ref = self.refFicha();
      if (!ref) return self.avisarFichaEnVuelo('No se ha cambiado el objetivo');
      Oeb.cambiarObjetivoDiarioEnFicha(ref.id, persona.codigoPersonal, { objetivoDiario: self.objetivoEditable }).then(function(res) {
        // La respuesta tampoco se pinta si para cuando llega la pantalla ya está en otro mes.
        if (!self.esElMismoMes(ref)) return;
        if (unwrapDto(res)) {
          self.$f7.toast.create({ text: 'Objetivo actualizado', position: 'top', closeTimeout: 2000 }).open();
          self.cargarFicha();
        }
      // #164 · El aviso sale aunque la pantalla haya cambiado de mes: lo que no se ha guardado no se
      // ha guardado en ningún mes, y callarlo es justo lo que hacía que se cambiaran objetivos
      // creyendo que quedaban puestos.
      }).catch(function(err) {
        avisoNoGuardado(self.$f7, 'el objetivo de ' + persona.codigoPersonal, err);
      });
    },
    // #85 · Con dos preguntas seguidas delante, aquí cabe de sobra un cambio de mes antes de que
    // se escriba nada: los días hábiles y su motivo van al mes que se estaba mirando al empezar, o
    // no van a ninguno. Es el mismo descuido que se corrigió en «+ Persona», «Guardar personas del
    // mes» y «Quitar del mes», y no es de personas, pero vive en la misma pantalla.
    editarDiaSemana: function(semana) {
      var self = this;
      var persona = self.personaSel;
      var ref = self.refFicha();
      if (!ref) return self.avisarFichaEnVuelo('No se ha cambiado nada');
      self.$f7.dialog.prompt('Nuevos d\u00edas h\u00e1biles (S' + semana.semanaNumero + '):', 'D\u00edas h\u00e1biles',
        function(dias) {
          self.$f7.dialog.prompt('Motivo del cambio:', 'Motivo',
            function(motivo) {
              if (!self.esElMismoMes(ref)) {
                self.$f7.dialog.alert('La pantalla ha cambiado de mes. No se han cambiado los días de nadie.', 'No se ha cambiado nada');
                return;
              }
              Oeb.modificarDiasHabiles(ref.id, persona.codigoPersonal, { semanaNumero: semana.semanaNumero, diasHabiles: parseInt(dias), motivo: motivo || 'Sin motivo' }).then(function(res) {
                if (!self.esElMismoMes(ref)) return;
                if (unwrapDto(res)) {
                  self.$f7.toast.create({ text: 'D\u00edas actualizados', position: 'top', closeTimeout: 2000 }).open();
                  self.cargarFicha();
                }
              }).catch(function(err) {
                avisoNoGuardado(self.$f7, 'los d\u00edas h\u00e1biles de ' + persona.codigoPersonal, err);
              });
            }, function() {}, 'Cambio de d\u00edas'
          );
        }, function() {}, semana.diasHabiles + ''
      );
    },
    verNotaDias: function(semana) {
      if (semana.modificacion) {
        this.$f7.dialog.alert('De ' + semana.modificacion.diasAnterior + ' a ' + semana.modificacion.diasNuevo + ' d\u00edas\nMotivo: ' + semana.modificacion.motivo + '\nPor: ' + semana.modificacion.usuario, 'Nota de modificaci\u00f3n');
      }
    },
    obtenerRangoSemana: function(semanaNumero) {
      if (!this.ficha || !this.ficha.calendario) return '';
      var sem = this.ficha.calendario.semanas.find(function(s) { return s.numero === semanaNumero; });
      if (!sem) return '';
      var match = sem.etiqueta.match(/\((\d+-\d+)\)/);
      return match ? match[1] : '';
    },
    calcularObjMensual: function() {
      if (!this.personaSel || !this.personaSel.semanas) return 0;
      var obj = this.objetivoEditable || 0;
      return this.personaSel.semanas.reduce(function(sum, s) { return sum + (obj * (s.diasHabiles || 0)); }, 0);
    },
    // #34 \u00b7 Quitar deja de ense\u00f1arla; sus cifras del mes se quedan donde estaban y vuelven si se
    // la a\u00f1ade otra vez.
    //
    // Se quita del mes que se estaba mirando al pulsar: si al confirmar la pantalla ya está en
    // otro, no se toca ninguno de los dos.
    quitarPersona: function(persona) {
      var self = this;
      var ref = self.refFicha();
      if (!ref) return self.avisarFichaEnVuelo('No se ha quitado a nadie');
      self.$f7.dialog.confirm('\u00bfQuitar a ' + persona.codigoPersonal + ' de este mes? Deja de verse, pero sus cifras no se borran: si se la vuelve a a\u00f1adir, siguen ah\u00ed.', 'Confirmar', function() {
        if (!self.esElMismoMes(ref)) {
          self.$f7.dialog.alert('La pantalla ha cambiado de mes. No se ha quitado a nadie.', 'No se ha quitado a nadie');
          return;
        }
        Oeb.quitarPersonaDeFicha(ref.id, persona.codigoPersonal).then(function() {
          if (!self.esElMismoMes(ref)) return;
          self.popupPersona = false;
          self.cargarFicha();
        }).catch(function(err) {
          avisoNoGuardado(self.$f7, 'que ' + persona.codigoPersonal + ' deje de verse este mes', err);
        });
      });
    }
  }
};
</script>

<style scoped>
/* C5: ficha de desempeño */
/* #166 · El relleno #23355b era casi el mismo azul que la cabecera y el botón no se leía como tal;
   con borde y fondo translúcido se distingue, y no compite con la X porque ni es un cuadrado ni
   lleva su mismo peso. */
.btn-desempeno {
  flex: 0 0 auto;
  padding: 6px 14px;
  background: rgba(255, 255, 255, .12);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, .35);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  /* #166 · La separación con la X va aquí y no con un `gap` en el contenedor: el flex gap tampoco
     existe en los navegadores del browserslist (Chrome 49, Firefox 31), y sin él los dos botones
     quedarían pegados. */
  margin-right: 12px;
}
.btn-desempeno:hover {
  background: rgba(255, 255, 255, .22);
}
.desemp-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
}
.desemp-lbl {
  display: block;
  font-size: 11px;
  color: #888;
}
.desemp-val {
  font-size: 16px;
  font-weight: 600;
}
.tendencia-barras {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  height: 120px;
}
.tendencia-col {
  flex: 1;
  text-align: center;
}
.tendencia-barra-wrap {
  height: 80px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.tendencia-barra {
  width: 70%;
  background: #0F6E56;
  border-radius: 3px 3px 0 0;
}
.tendencia-sindato {
  background: repeating-linear-gradient(45deg, #ddd, #ddd 4px, #eee 4px, #eee 8px);
}
.tendencia-mes {
  font-size: 10px;
  color: #888;
  margin-top: 2px;
}
.tendencia-pct {
  font-size: 11px;
  font-weight: 600;
}
.desemp-marcas {
  font-size: 11px;
  color: #b26a00;
}

/* #16 · sello de "de cuando son estos datos", mismo sitio en todas las pantallas. */
.sello-datos-bar { padding: 6px 16px 0; }
/* Dentro del popup va pegado a la cabecera oscura, sin el hueco superior de la pantalla. */
.sello-datos-bar--popup { padding: 8px 12px 0; }
</style>
